import {
	hasPersistedUploadFacadeErrorCode,
	isUploadTerminalFailure,
	readUploadRefusalTrigger,
	readUploadTerminalDetails,
	SETTLING_FACADE_ERROR_CODES,
	withFacadeErrorCodeInMessage
} from "./uploadFacadeErrors";
import { indexedDbErrorChainLogDetails } from "./indexedDbErrors";
import { probeRefusedUploadAttempt } from "./refusedAttemptProbe";
import { isUploadControlUnavailable } from "./uploadKnownSizeControl";
import { isUploadVerificationPending, verificationPendingRetryOptions } from "./uploadVerificationPending";
import type {
	ChunkedUploadDriver,
	ChunkedUploadDriverPorts,
	DriverChunkMeta,
	DriverFile,
	DriverResumeOutcome,
	DriverUploadOutcome,
	MultipartTargetValidationFailure,
	MultipartSessionShape,
	NewSessionInput,
	PersistableChunkSource,
	TargetAttachmentResult,
	UploadLease,
	UploadLeaseScope,
	UploadFileCategory
} from "./ports";
import {
	MAX_MULTIPART_PARTS,
	createUploadTargetAdapter,
	getUploadEndpointFamily,
	resolveUploadTargetAdapter,
	uploadTargetChunkUrls,
	uploadTargetFamily,
	isUploadsUploadTarget,
	type FamilyAwareMultipartUploadTarget,
	type MultipartUploadTarget
} from "./targetInitiation";
import {
	createChunkedUploadDriverControlFlow,
	type ChunkedUploadDriverControlFlow,
	type UploadSingleFlightAttempt
} from "./driverControlFlow";
import {
	finalizeMultipartCompletion as finalizeCompletionTransition,
	resolveCompletionOutcome,
	resolveMultipartCompletionRequest
} from "./completionTransition";
import {
	logResumeWarning,
	markPersistedSessionTerminalComplete,
	recoverGoneMultipartForFailFastPolicy,
	assertUsablePersistedTotalChunks,
	recoversGoneMultipartByProbe,
	resumeFileUpload as resumePersistedFileUpload,
	applyPartsIncompleteRemark
} from "./driverResume";
import { CHUNK_SIZE } from "./chunkSize";
import { persistedSessionStableId } from "./ports";
import { createUploadedChunkBytes, uploadPendingChunks, validatePersistedChunkByteIntegrity, type UploadedPart } from "./driverChunkLoop";
import { createPartReceiptRecovery } from "./partReceiptRecovery";
import { runGuardedEffect } from "./ownershipGate";
import {
	decideReconciliationTerminal,
	reconcilePersistedSession,
	type ListPartsEvidence,
	type ReconciliationEvidence
} from "./reconciliationStateMachine";
import {
	UploadCancelledError,
	ChunkNetworkError,
	FileUnreadableError,
	MissingEtagError,
	MultipartUploadGoneError,
	PersistedByteIntegrityError,
	S3HttpError,
	TruncatedPartsError,
	UploadOwnershipLostError,
	type ChunkSourceFailureKind
} from "./uploadChunk";
import { getInvalidMultipartTargetIdentityField, hasValidMultipartUrls, hasValidMultipartParts, isValidEtag } from "./multipartValidation";
import {
	assertExactPresignedUrls,
	isConfirmedMigrationIntegrityError,
	isMultipartUrlRefreshError,
	prepareDirectUploadUrls,
	preparePersistedUploadUrls,
	parsePersistedChunkUrls
} from "./driverUrlRefresh";
import { persistDriverFileStream } from "./sequentialStreamSource";
import { PersistedSourceReadStrategyUnavailableError, selectSourceReadStrategy, type StreamCapableDriverFile } from "./sourceReadStrategy";
import { RESUME_OUTCOME } from "./resumeSession";
import { computeSlowTierRetryAt } from "./retryPolicy";
import {
	isChunkedObjectAssemblyIntegrityError,
	isUploadAlreadyCompleteAtCreate,
	isUploadPartsIncompleteError,
	isUploadProgressConflict
} from "./uploadFacadeErrors";
import {
	MAX_CONSECUTIVE_ASSEMBLY_INTEGRITY_FAILURES,
	clearAssemblyIntegrityFailures,
	escalateUnproductivePartsIncomplete,
	recordAssemblyIntegrityFailure,
	recordPartsIncompleteRefusal
} from "./partsIncompleteBound";

function isRetryOutcome(outcome: unknown): boolean {
	return outcome === "retry" || outcome === RESUME_OUTCOME.RETRY;
}

type MultipartTargetValidationResult =
	| {
			ok: true;
			requiredChunks: number;
			targetChunkCount: number;
	  }
	| MultipartTargetValidationFailure;

interface ChunkedUploadFileInput<FileLike extends DriverFile, LeaseToken> {
	file: FileLike;
	target: FamilyAwareMultipartUploadTarget;
	conversationId?: string;
	batchId?: string;
	uploadScope?: { organizationId?: string; userId?: string };
	fileCategory?: UploadFileCategory;
	onProgress(bytesUploaded: number): void;
	cancelSignal?: AbortSignal;
	releaseActiveOnFinally?: boolean;
	leaseToken?: LeaseToken;
	/** See ChunkedUploadDriver.persistFileToIndexedDB's identical parameter (LSP-17735). */
	detectedMimeType?: string;
	captureEvidence?: Record<string, unknown>;
}

type PendingTerminalEvidence =
	| Omit<Extract<ReconciliationEvidence, { kind: "local-gaps" }>, "listParts">
	| Omit<Extract<ReconciliationEvidence, { kind: "read-failure" }>, "listParts">;

class StreamPersistenceOwnershipLostError extends Error {
	constructor() {
		super("Upload lease ownership changed while persisting a sequential source");
		this.name = "StreamPersistenceOwnershipLostError";
	}
}

function assertValidMultipartTargetIdentity(target: FamilyAwareMultipartUploadTarget, operation: string): void {
	if (uploadTargetFamily(target) === "uploads") {
		createUploadTargetAdapter("uploads").normalize(target as Extract<FamilyAwareMultipartUploadTarget, { family: "uploads" }>);
		return;
	}
	const invalidField = getInvalidMultipartTargetIdentityField(target as MultipartUploadTarget);
	if (invalidField) throw new Error(`${operation} returned invalid ${invalidField}`);
}

function isTargetAttachmentResult<Session>(value: Session | TargetAttachmentResult<Session>): value is TargetAttachmentResult<Session> {
	return (
		typeof value === "object" &&
		value !== null &&
		"kind" in value &&
		((value as { kind?: unknown }).kind === "attached" || (value as { kind?: unknown }).kind === "skipped-not-owned")
	);
}

/**
 * Header-first scheduling for chunked-object uploads: the persisted permutation from
 * `headerFirstChunkOrder` decides which pending chunks go first. Indexes the permutation does not
 * name (or a malformed value) keep the caller's own order, so this can never lose a chunk.
 *
 * Deliberately order-PRESERVING rather than sorting the remainder ascending. The pending list on the
 * resume path is whatever `listChunkMeta` returned, and re-sorting it changes which chunk a pass
 * attempts first — which changes which chunk's failure stops the pool, and therefore which parts
 * exist when the completion runs (codex-batch4's post-Gone partial-parts probe pins exactly that).
 * A row with no persisted permutation must schedule exactly as it did before this function existed.
 */
export function orderPendingChunks<Chunk extends { chunkIndex: number }>(
	pendingChunks: readonly Chunk[],
	priorityChunkIndexesJson: string | undefined
): Chunk[] {
	const asGiven = [...pendingChunks];
	if (!priorityChunkIndexesJson) return asGiven;
	let priority: unknown;
	try {
		priority = JSON.parse(priorityChunkIndexesJson);
	} catch {
		return asGiven;
	}
	if (!Array.isArray(priority)) return asGiven;
	// Grouped by index rather than keyed one-to-one: a chunk grid read back from IndexedDB can carry
	// TWO metadata rows for the same chunkIndex (a legacy duplicate, an interrupted grid rewrite), and
	// a one-to-one Map would silently drop all but the last — the caller would then upload fewer parts
	// than it counted and the completion could never verify. Ordering is a preference; losing a
	// pending entry is not.
	const byIndex = new Map<number, Chunk[]>();
	for (const chunk of asGiven) {
		const existing = byIndex.get(chunk.chunkIndex);
		if (existing) existing.push(chunk);
		else byIndex.set(chunk.chunkIndex, [chunk]);
	}
	const ordered: Chunk[] = [];
	for (const value of priority) {
		if (!Number.isInteger(value)) continue;
		const chunks = byIndex.get(value as number);
		if (chunks) {
			ordered.push(...chunks);
			byIndex.delete(value as number);
		}
	}
	for (const chunks of byIndex.values()) ordered.push(...chunks);
	return ordered;
}

/** `releasePersistedSessionForRetry` failureKind for a chunked-object `UPLOAD_PARTS_INCOMPLETE` refusal. */
export const PARTS_INCOMPLETE_FAILURE_KIND = "parts-incomplete";

export function validateMultipartTarget(
	input: {
		fileSize: number;
		chunkSize: number;
	} & (
		| { targetChunkCount: number }
		| {
				targetChunkUrls: unknown;
				uploadId: unknown;
				s3Key: unknown;
				bucket: unknown;
		  }
	)
): MultipartTargetValidationResult {
	const requiredChunks = Math.ceil(input.fileSize / input.chunkSize);
	const validatesUrlContents = "targetChunkUrls" in input;
	const targetChunkCount = validatesUrlContents
		? Array.isArray(input.targetChunkUrls)
			? input.targetChunkUrls.length
			: 0
		: input.targetChunkCount;
	if (!Number.isFinite(input.fileSize) || !Number.isFinite(input.chunkSize) || input.chunkSize <= 0 || input.fileSize <= 0) {
		return {
			ok: false,
			reason: "empty-file",
			requiredChunks,
			targetChunkCount
		};
	}
	if (!Number.isInteger(targetChunkCount) || targetChunkCount <= 0 || !Number.isFinite(requiredChunks)) {
		return {
			ok: false,
			reason: "invalid-urls",
			requiredChunks,
			targetChunkCount
		};
	}
	if (requiredChunks > MAX_MULTIPART_PARTS) {
		return {
			ok: false,
			reason: "too-many-parts",
			requiredChunks,
			targetChunkCount
		};
	}
	if (validatesUrlContents) {
		const invalidIdentityField = getInvalidMultipartTargetIdentityField(input);
		if (invalidIdentityField) {
			return {
				ok: false,
				reason:
					invalidIdentityField === "uploadId"
						? "invalid-upload-id"
						: invalidIdentityField === "s3Key"
							? "invalid-s3-key"
							: "invalid-bucket",
				requiredChunks,
				targetChunkCount
			};
		}
	}
	if (targetChunkCount < requiredChunks) {
		return {
			ok: false,
			reason: "too-few-urls",
			requiredChunks,
			targetChunkCount
		};
	}
	if (targetChunkCount > requiredChunks) {
		return {
			ok: false,
			reason: "extra-urls",
			requiredChunks,
			targetChunkCount
		};
	}
	if (validatesUrlContents && !hasValidMultipartUrls(input.targetChunkUrls, requiredChunks)) {
		return {
			ok: false,
			reason: "invalid-urls",
			requiredChunks,
			targetChunkCount
		};
	}
	return {
		ok: true,
		requiredChunks,
		targetChunkCount
	};
}

// LSP-18742 (ls-heroku-ci re-review): "missing-etag" is surfaced here too, not only on the innermost
// per-attempt log. The PR's stated triage workflow is a Datadog filter on failureKind, and collapsing
// this class to "http" made that filter miss the very sites a frozen session is investigated from.
// LSP-23090: contact-admin's IndexedDB stall (IdbStallError, matched by name because this package cannot import the
// app's class) is local storage, not the network or the server; reporting it as "http" sent triage to the wrong layer.
export function classifyDirectUploadFailureKind(error: unknown): "network" | "http" | "missing-etag" | "local-storage-stall" {
	if (error instanceof TypeError || error instanceof ChunkNetworkError) {
		return "network";
	}
	if (error instanceof MissingEtagError) return "missing-etag";
	if (error instanceof Error && error.name === "IdbStallError") return "local-storage-stall";
	return error instanceof Error && error.name === "AbortError" ? "network" : "http";
}

function directUploadHttpStatus(error: unknown): number | undefined {
	return error instanceof S3HttpError ? error.status : undefined;
}

function unwrapMultipartUrlRefreshError(error: unknown): unknown {
	return isMultipartUrlRefreshError(error) && error.originalError !== undefined ? error.originalError : error;
}

function isUploadLeaseLostError(error: unknown): boolean {
	return error instanceof Error && error.name === "UploadLeaseLostError";
}

function matchesUploadIdentity(session: MultipartSessionShape, file: DriverFile, target: FamilyAwareMultipartUploadTarget): boolean {
	// Deliberately does NOT compare session.fileType against file.type (round 76 / LSP-17735).
	// persistUploadSession can now write a magic-byte-DETECTED MIME into the durable record
	// (round 75) that legitimately, by design, differs from the browser-reported file.type for
	// exactly the spoofed-file cases that detection exists to correct (a renamed extension, or an
	// empty file.type some pickers report). Comparing the two here treated that correction as an
	// identity MISMATCH, sending the file through a throwaway ephemeral upload and leaving the
	// durable row falsely demoted to "pending" with an already-completed uploadId — silently
	// voiding the resumability guarantee for precisely the files this whole detection path exists
	// to protect. uploadId/s3Key/bucket/fileName/fileSize already uniquely pin which file this row
	// is; fileType was never load-bearing for that identity check.
	const targetAdapter = resolveUploadTargetAdapter(target);
	if (targetAdapter.family === "uploads") {
		return (
			session.endpointFamily === "uploads" &&
			session.uploadId === targetAdapter.opaqueId(target) &&
			session.fileName === file.name &&
			session.fileSize === file.size
		);
	}
	return (
		session.endpointFamily !== "uploads" &&
		session.uploadId === (target as MultipartUploadTarget).uploadId &&
		session.s3Key === (target as MultipartUploadTarget).s3Key &&
		session.bucket === (target as MultipartUploadTarget).bucket &&
		session.fileName === file.name &&
		session.fileSize === file.size
	);
}

function uploadIdentityMismatchError(session: MultipartSessionShape, file: DriverFile): Error {
	return new Error(
		`Refusing to read persisted storage from multipart session ${session.id} for a different file identity (${file.name}, ${file.size} bytes)`
	);
}

type DirectUploadLeaseScope<Session, LeaseToken> = Omit<UploadLeaseScope<Session, LeaseToken>, "token"> & {
	readonly token?: LeaseToken;
};

function createEphemeralUploadLeaseScope<Session extends MultipartSessionShape>(): DirectUploadLeaseScope<Session, never> {
	const lease: UploadLease<Session> = {
		claim: async () => true,
		touch: async () => undefined,
		owns: async () => true,
		ownerLiveness: async () => "alive"
	};
	return {
		lease,
		activate: () => undefined,
		runWithLiveness: async (_session, work) => work(),
		release: () => undefined
	};
}

export function createChunkedUploadDriver<FileLike extends DriverFile, Session extends MultipartSessionShape, LeaseToken>(
	ports: ChunkedUploadDriverPorts<FileLike, Session, LeaseToken>
): ChunkedUploadDriver<FileLike, Session, LeaseToken> & {
	readonly controlFlow: ChunkedUploadDriverControlFlow;
} {
	const areTerminalEffectsAllowed = (session: Session, operation: string): boolean =>
		(
			ports.policies as typeof ports.policies & {
				areTerminalEffectsAllowed?: (session: Session, operation: string) => boolean;
			}
		).areTerminalEffectsAllowed?.(session, operation) ?? true;
	const controlFlow = createChunkedUploadDriverControlFlow();
	const ACTIVITY_COOLDOWN_MS = 60_000;
	// Reserve at pick (docs/plans/2026-10-16-web-lazy-multipart-initiate.md). In-flight reservations by
	// session id, so this driver's own later mint for a session never overtakes its reservation request.
	const pendingReservations = new Map<string, Promise<void>>();

	/**
	 * Best-effort, unawaited: the reservation only makes the server's wait-for-uploads gate and reminder
	 * see this file before its bytes start. Offline, refused or slow, the later mint creates the row
	 * anyway — so nothing here may block or fail preparation.
	 */
	function reserveDeferredSession(session: Session): void {
		const reservePersisted = ports.persistedTargetInitiator.reservePersisted;
		if (!reservePersisted) return;
		if (session.endpointFamily !== "uploads") {
			// Legacy-pinned rows initiate through the legacy routes, which have no reserve mode.
			ports.telemetry.logInfo("recording.fileUpload.reserveSkipped", { sessionId: session.id, reason: "legacy-family" });
			return;
		}
		const stableId = persistedSessionStableId(session);
		const reservation = Promise.resolve()
			.then(() => reservePersisted.call(ports.persistedTargetInitiator, { session, stableId, totalChunks: session.totalChunks }))
			.then(
				() => ports.telemetry.logInfo("recording.fileUpload.reserved", { sessionId: session.id, fileCategory: session.fileCategory }),
				(error: unknown) =>
					// Info, not warn: an offline pick is the expected way to land here, and the lazy mint recovers it.
					ports.telemetry.logInfo("recording.fileUpload.reserveDeferred", {
						sessionId: session.id,
						fileCategory: session.fileCategory,
						reason: error instanceof Error ? error.message : String(error)
					})
			)
			.catch(() => undefined)
			.finally(() => {
				if (pendingReservations.get(session.id) === reservation) pendingReservations.delete(session.id);
			});
		pendingReservations.set(session.id, reservation);
	}

	const RESERVATION_WAIT_MS = 10_000;
	async function awaitPendingReservation(sessionId: string): Promise<void> {
		const reservation = pendingReservations.get(sessionId);
		if (!reservation) return;
		let timer: ReturnType<typeof setTimeout> | undefined;
		await Promise.race([reservation, new Promise<void>((resolve) => (timer = setTimeout(resolve, RESERVATION_WAIT_MS)))]);
		if (timer !== undefined) clearTimeout(timer);
	}
	const ACTIVITY_TIMEOUT_MS = 15_000;

	function createActivityReporter(session: Session, lease: UploadLease<Session>): { signal(): void; stop(): void } {
		let lastStartedAt = Number.NEGATIVE_INFINITY;
		let pendingVersion = 0;
		let sentVersion = 0;
		let inFlight = false;
		let timer: ReturnType<typeof setTimeout> | undefined;
		let stopped = false;
		let activeController: AbortController | undefined;

		const trySend = (): void => {
			if (stopped || inFlight || sentVersion === pendingVersion || !ports.multipartApi.progress) return;
			const delay = lastStartedAt + ACTIVITY_COOLDOWN_MS - ports.now();
			if (delay > 0) {
				if (!timer)
					timer = setTimeout(() => {
						timer = undefined;
						trySend();
					}, delay);
				return;
			}
			const version = pendingVersion;
			let reported = false;
			inFlight = true;
			const controller = new AbortController();
			activeController = controller;
			const timeout = setTimeout(() => controller.abort(), ACTIVITY_TIMEOUT_MS);
			void (async () => {
				const owns = await lease.owns(session);
				if (!owns || stopped) return;
				lastStartedAt = ports.now();
				await ports.multipartApi.progress!(session, {}, { signal: controller.signal });
				reported = true;
			})()
				.catch((error) => {
					if (stopped) return;
					if (isUploadProgressConflict(error)) {
						// LSP-19605: the server's lifecycle gate no longer accepts progress for this attempt —
						// usually because `/complete` began (this tab's or a sibling's) before this heartbeat
						// landed. The heartbeat is not the authority on the session's fate (the complete/urls
						// calls are, and they log at their own level), so this is an info breadcrumb, kept as
						// its own event so the rate stays countable, not a warn.
						ports.telemetry.logInfo("uploads.multipartActivity.reportRefusedByLifecycle", {
							sessionId: session.id,
							attemptId: session.attemptId
						});
						return;
					}
					ports.telemetry.logWarn(
						"uploads.multipartActivity.reportFailed",
						{ sessionId: session.id, attemptId: session.attemptId },
						error
					);
				})
				.finally(() => {
					clearTimeout(timeout);
					if (activeController === controller) activeController = undefined;
					// A failed report consumes every signal already observed. Only byte movement or a
					// durable part checkpoint that happens after the failure may retry it; otherwise an
					// upload stalled inside one PUT would manufacture liveness forever.
					sentVersion = reported ? version : pendingVersion;
					inFlight = false;
					if (!stopped) trySend();
				});
		};

		return {
			signal() {
				if (stopped || session.endpointFamily !== "uploads") return;
				pendingVersion++;
				trySend();
			},
			stop() {
				stopped = true;
				if (timer) clearTimeout(timer);
				timer = undefined;
				activeController?.abort();
			}
		};
	}

	async function createSession(
		input: NewSessionInput,
		leaseScope: DirectUploadLeaseScope<Session, LeaseToken>
	): Promise<{ session: Session; created: boolean }> {
		if (input.ephemeral) {
			if (!input.target) {
				throw new Error("Ephemeral direct upload requires a target");
			}
			return {
				session: ports.createEphemeralSession({
					...input,
					ephemeral: true,
					target: input.target
				}),
				created: true
			};
		}
		return ports.sessionStore.createSession(input, leaseScope.token!);
	}

	async function persistChunkFromSource(input: {
		session: Session;
		source: PersistableChunkSource;
		chunkIndex: number;
		signal: AbortSignal;
		onSourceFailure(kind: ChunkSourceFailureKind): Error;
		ownsBeforePersist?(): Promise<boolean>;
		ownsAfterPersist?(): Promise<boolean>;
		commitIfOwned?(effect: () => Promise<void>): Promise<"executed" | "skipped-not-owned">;
		onPersisted?(chunk: DriverChunkMeta): void;
	}): Promise<boolean> {
		const sourceResult = await input.source.readBuffer(input.chunkIndex, input.signal);
		if (!sourceResult.ok) {
			throw input.onSourceFailure(sourceResult.kind);
		}
		return persistChunkBuffer({
			session: input.session,
			chunkIndex: input.chunkIndex,
			buffer: sourceResult.buffer,
			ownsBeforePersist: input.ownsBeforePersist,
			ownsAfterPersist: input.ownsAfterPersist,
			commitIfOwned: input.commitIfOwned,
			onPersisted: input.onPersisted
		});
	}

	async function persistChunkBuffer(input: {
		session: Session;
		chunkIndex: number;
		buffer: ArrayBuffer;
		ownsBeforePersist?(): Promise<boolean>;
		ownsAfterPersist?(): Promise<boolean>;
		commitIfOwned?(effect: () => Promise<void>): Promise<"executed" | "skipped-not-owned">;
		onPersisted?(chunk: DriverChunkMeta): void;
	}): Promise<boolean> {
		if (input.buffer.byteLength === 0) {
			throw new Error(`Cannot persist a zero-byte chunk (chunkIndex=${input.chunkIndex})`);
		}
		if (input.ownsBeforePersist && !(await input.ownsBeforePersist())) {
			return false;
		}
		const chunk: DriverChunkMeta = {
			chunkIndex: input.chunkIndex,
			size: input.buffer.byteLength,
			isUploaded: false,
			etag: null
		};
		const persist = () =>
			ports.sessionStore.persistChunk(input.session, {
				chunkIndex: chunk.chunkIndex,
				size: chunk.size,
				data: input.buffer
			});
		if (input.commitIfOwned && (await input.commitIfOwned(persist)) === "skipped-not-owned") return false;
		if (!input.commitIfOwned) await persist();
		if (input.ownsAfterPersist && !(await input.ownsAfterPersist())) {
			return false;
		}
		input.onPersisted?.(chunk);
		return true;
	}

	const verifyReinitializable = (session: Session) =>
		session.ephemeral ? Promise.resolve("guaranteed" as const) : ports.sessionStore.verifyReinitializable(session);

	function requiredChunksOrThrow(file: FileLike, target: FamilyAwareMultipartUploadTarget): number {
		const targetAdapter = resolveUploadTargetAdapter(target);
		const chunkUrls = targetAdapter.chunkUrls(target);
		if (targetAdapter.family === "uploads") {
			const requiredChunks = Math.ceil(file.size / CHUNK_SIZE);
			if (requiredChunks <= 0 || requiredChunks > MAX_MULTIPART_PARTS || chunkUrls.length !== requiredChunks) {
				throw ports.errors.createMultipartTargetValidationError(
					{
						ok: false,
						reason:
							chunkUrls.length < requiredChunks
								? "too-few-urls"
								: chunkUrls.length > requiredChunks
									? "extra-urls"
									: "invalid-urls",
						requiredChunks,
						targetChunkCount: chunkUrls.length
					},
					file
				);
			}
			return requiredChunks;
		}
		const validation = validateMultipartTarget({
			fileSize: file.size,
			targetChunkUrls: chunkUrls,
			uploadId: (target as MultipartUploadTarget).uploadId,
			s3Key: (target as MultipartUploadTarget).s3Key,
			bucket: (target as MultipartUploadTarget).bucket,
			chunkSize: CHUNK_SIZE
		});
		if (validation.ok === false) {
			throw ports.errors.createMultipartTargetValidationError(validation, file);
		}
		return validation.requiredChunks;
	}

	async function persistUploadSession(input: {
		file: FileLike;
		onSessionCreated?: (session: Session) => void | Promise<void>;
		target?: FamilyAwareMultipartUploadTarget;
		conversationId: string;
		batchId?: string;
		uploadScope?: { organizationId?: string; userId?: string };
		fileCategory: UploadFileCategory;
		totalChunks: number;
		phase: "pre-persist" | "deferred";
		projectChecklistItemId?: string;
		photoTime?: string;
		captureEvidence?: Record<string, unknown>;
		detectedMimeType?: string;
		onPreparationProgress?: (chunksPersisted: number, totalChunks: number) => void;
	}): Promise<string> {
		// Resolved from `input.target`, which this function never reassigns, so the one adapter is
		// valid for every use below.
		const targetAdapter = input.target ? resolveUploadTargetAdapter(input.target) : undefined;
		if (input.target && targetAdapter) {
			const existingSession = await ports.sessionStore.findByUploadId(targetAdapter.opaqueId(input.target));
			if (existingSession) {
				if (!matchesUploadIdentity(existingSession, input.file, input.target)) {
					throw uploadIdentityMismatchError(existingSession, input.file);
				}
				return existingSession.id;
			}
		}

		const sessionId = ports.createId();
		const leaseScope = ports.leaseCoordinator.createScope("pre-persist", sessionId);
		const preparationStartedAt = Date.now();
		let preparationOutcome = "failed";
		const preparationContext = { sessionId, preparationPhase: input.phase, fileSize: input.file.size, expectedChunkCount: input.totalChunks };
		const observePreparation = (event: string, context: Record<string, unknown>) => {
			try {
				ports.telemetry.logInfo(event, context);
			} catch {
				// Diagnostic only: a failed sink must not redirect preparation.
			}
		};
		observePreparation("recording.fileUpload.preparationStarted", preparationContext);
		let cloudHosted: boolean;
		try {
			cloudHosted = await ports.chunkSources.detectCloudHosted(input.file, { sessionId, phase: input.phase });
		} catch (error) {
			observePreparation("recording.fileUpload.preparationFinished", {
				...preparationContext,
				preparationOutcome: "probe-failed",
				sourceReadStrategy: "unknown",
				preparationDurationMs: Math.max(0, Date.now() - preparationStartedAt)
			});
			throw error;
		}
		// Select once, before the durable row is created. Resume/adoption must retain this
		// provenance instead of re-running a potentially different capability probe later.
		const readStrategy = selectSourceReadStrategy(input.file as StreamCapableDriverFile, cloudHosted, {
			maxArrayBufferBytes: ports.maxCloudArrayBufferBytes,
			largeFileThresholdBytes: ports.maxCloudArrayBufferBytes
		});
		const newSession: NewSessionInput = {
			id: sessionId,
			batchId: input.batchId,
			uploadScope: input.uploadScope,
			conversationId: input.conversationId,
			fileName: input.file.name,
			fileSize: input.file.size,
			// Prefer the caller's already-verified content-sniffed type over the FileLike's own
			// (spoofable) reported type — a background drain later mints this session's target from
			// this durable record alone, with no access to the original File to re-sniff (LSP-17735).
			fileType: input.detectedMimeType || input.file.type || "application/octet-stream",
			fileCategory: input.fileCategory,
			target: input.target,
			totalChunks: input.totalChunks,
			cloudHosted,
			sourceReadStrategy: readStrategy,
			phase: input.phase,
			stableId: sessionId,
			endpointFamily: targetAdapter?.family ?? "legacy",
			projectChecklistItemId: input.projectChecklistItemId,
			photoTime: input.photoTime,
			// The upload session is the canonical source identity used by the manifest API. Normalize
			// the inspector's provisional id here so retries and server correlation survive a tab exit.
			captureEvidence: input.captureEvidence ? { ...input.captureEvidence, sourceId: sessionId } : undefined,
			detectedMimeType: input.detectedMimeType
		};

		const sourceReadStrategy = readStrategy === "arrayBuffer" ? "array-buffer" : readStrategy === "slice" ? "chunked-slice" : "stream";
		observePreparation("recording.fileUpload.preparationStrategy", { ...preparationContext, sourceReadStrategy });

		let session: Session | undefined;
		let sessionWasCreated = false;
		let sourceFailureKind: ChunkSourceFailureKind | undefined;
		try {
			const created = await createSession(newSession, leaseScope);
			session = created.session;
			sessionWasCreated = created.created;
			if (input.target && !matchesUploadIdentity(session, input.file, input.target)) {
				throw uploadIdentityMismatchError(session, input.file);
			}
			if (!created.created) {
				preparationOutcome = "existing-session";
				return session.id;
			}
			leaseScope.activate(session);
			if (input.onSessionCreated) await input.onSessionCreated(session);
			if (input.phase === "deferred" && !input.target) reserveDeferredSession(session);

			const persistedByOwner = await leaseScope.runWithLiveness(session, async (): Promise<boolean> => {
				const wholeFile =
					readStrategy === "arrayBuffer"
						? await ports.chunkSources.readWholeFile(input.file, new AbortController().signal)
						: undefined;

				if (readStrategy === "stream") {
					let ownershipWasLost = false;
					try {
						await persistDriverFileStream(input.file as StreamCapableDriverFile, {
							partSize: CHUNK_SIZE,
							signal: new AbortController().signal,
							persistChunk: async (part) => {
								const persisted = await persistChunkBuffer({
									session: session!,
									chunkIndex: part.chunkIndex,
									buffer: part.buffer,
									ownsBeforePersist: () => leaseScope.lease.owns(session!),
									ownsAfterPersist: () => leaseScope.lease.owns(session!),
									commitIfOwned: async (effect) =>
										(await runGuardedEffect(leaseScope.lease, session!, effect)) === "skipped-not-owned"
											? "skipped-not-owned"
											: "executed"
								});
								if (!persisted) {
									ownershipWasLost = true;
									throw new StreamPersistenceOwnershipLostError();
								}
								input.onPreparationProgress?.(part.chunkIndex + 1, input.totalChunks);
							}
						});
					} catch (error) {
						if (ownershipWasLost || error instanceof StreamPersistenceOwnershipLostError) return false;
						if (error instanceof FileUnreadableError) sourceFailureKind = "unreadable";
						throw error;
					}
				} else {
					const chunkSource: PersistableChunkSource = ports.chunkSources.forPersistence(input.file, wholeFile);

					for (let chunkIndex = 0; chunkIndex < input.totalChunks; chunkIndex++) {
						const persisted = await persistChunkFromSource({
							session: session!,
							source: chunkSource,
							chunkIndex,
							signal: new AbortController().signal,
							onSourceFailure: (kind) => {
								sourceFailureKind = kind;
								return ports.errors.createChunkSourceError(kind, input.file.name);
							},
							ownsBeforePersist: () => leaseScope.lease.owns(session!),
							ownsAfterPersist: () => leaseScope.lease.owns(session!),
							commitIfOwned: async (effect) =>
								(await runGuardedEffect(leaseScope.lease, session!, effect)) === "skipped-not-owned"
									? "skipped-not-owned"
									: "executed"
						});
						if (!persisted) return false;
						input.onPreparationProgress?.(chunkIndex + 1, input.totalChunks);
					}
				}
				if (
					(await runGuardedEffect(leaseScope.lease, session!, () => ports.sessionStore.finishPrePersist(session!))) ===
					"skipped-not-owned"
				)
					return false;
				return true;
			});

			if (!persistedByOwner) {
				preparationOutcome = "ownership-lost";
				// Ownership was lost mid-persist (another owner's CAS won) — this scope's own
				// in-memory activation is moot either way, so release it here too, not only on
				// the error path below.
				leaseScope.release(session);
				ports.telemetry.logWarn("recording.fileUpload.yielded", {
					reason: "pre-persist-ownership-lost",
					sessionId: session.id,
					uploadId: session.uploadId
				});
				return session.id;
			}

			// finishPrePersist() already released the DURABLE (DB-level) lease to hand this
			// session off to the drain — this scope's in-memory activation must be released in
			// the same breath, or its heartbeat interval keeps running (and IndexedDB-writing)
			// for the rest of the tab's life with nothing left for it to protect.
			leaseScope.release(session);
			preparationOutcome = "complete";
			return session.id;
		} catch (error) {
			if (error instanceof FileUnreadableError) {
				sourceFailureKind = "unreadable";
			}
			if (session && sessionWasCreated) {
				if (sourceFailureKind === "unreadable") {
					await runGuardedEffect(leaseScope.lease, session, async () => {
						await ports.sessionStore
							.deleteAllChunkData(session!)
							.catch((cleanupError) =>
								logBestEffortFailure("delete-unreadable-pre-persist-chunks", session, input.file.name, cleanupError)
							);
						await ports.sessionStore.tombstoneCancelled(session!, {
							notifyOtherTabs: false
						});
					}).catch((cleanupError) =>
						logBestEffortFailure("tombstone-unreadable-pre-persist-session", session, input.file.name, cleanupError)
					);
				} else {
					await ports.sessionStore
						.abandonSetup(session)
						.catch((cleanupError) =>
							logBestEffortFailure("abandon-pre-persist-session", session, input.file.name, cleanupError)
						);
				}
				leaseScope.release(session);
			}

			const storageFailure = ports.errors.classifyStorage(error);
			if (storageFailure === "internal" && input.phase === "pre-persist") {
				try {
					ports.telemetry.logWarn("recording.fileUpload.prePersistStorageFailure", {
						sessionId: session?.id,
						storageFailure,
						...indexedDbErrorChainLogDetails(error)
					});
				} catch {
					// Diagnostics must preserve the classifier's decision and original failure.
				}
			}
			if (sourceFailureKind === "unreadable") {
				ports.telemetry.logError(
					`[Upload] "${input.file.name}" — file bytes could not be read (likely cloud-offloaded/stale reference)`,
					{
						sessionId: session?.id,
						uploadId: session?.uploadId ?? (input.target && targetAdapter ? targetAdapter.opaqueId(input.target) : undefined),
						fileName: input.file.name
					},
					error
				);
			} else if (storageFailure === "quota") {
				ports.telemetry.logWarn(
					`[Upload] "${input.file.name}" — IDB quota exceeded; upload can proceed without resume support if confirmed`,
					{
						sessionId: session?.id,
						uploadId: session?.uploadId ?? (input.target && targetAdapter ? targetAdapter.opaqueId(input.target) : undefined),
						fileName: input.file.name
					},
					error
				);
				throw new Error(`QuotaExceeded:${input.file.name}`);
			} else if (storageFailure === "internal" && input.phase === "pre-persist") {
				ports.telemetry.logError(
					`[Upload] "${input.file.name}" — local storage was unavailable while persisting chunks; resume protection for this file was not saved`,
					{
						sessionId: session?.id,
						uploadId: session?.uploadId ?? (input.target && targetAdapter ? targetAdapter.opaqueId(input.target) : undefined),
						fileName: input.file.name
					},
					error
				);
				throw new Error(
					`Local storage was unavailable while persisting "${input.file.name}". Resume protection for this file was not saved.`
				);
			}
			throw error;
		} finally {
			observePreparation("recording.fileUpload.preparationFinished", {
				...preparationContext,
				sessionId: session?.id ?? sessionId,
				sourceReadStrategy,
				preparationDurationMs: Math.max(0, Date.now() - preparationStartedAt),
				preparationOutcome
			});
		}
	}

	function logBestEffortFailure(operation: string, session: Session | undefined, fileName: string, error: unknown): void {
		try {
			ports.telemetry.logWarn(
				"recording.fileUpload.cleanupFailed",
				{
					operation,
					sessionId: session?.id,
					uploadId: session?.uploadId,
					fileName
				},
				error
			);
		} catch {
			// Best-effort telemetry must never replace the failure that triggered cleanup.
		}
	}

	function logCompletionCleanupFailure(session: Session, error: unknown): void {
		try {
			ports.telemetry.logWarn(
				`[Upload] "${session.fileName}" completed on S3 but IDB cleanup failed — session is durably complete and will not re-upload or re-complete`,
				{
					sessionId: session.id,
					uploadId: session.uploadId,
					fileName: session.fileName
				},
				error
			);
		} catch {
			// Cleanup telemetry must not replace a successful multipart completion.
		}
	}

	/**
	 * LSP-22828: the server answered a re-create of this session's upload with `alreadyComplete` — it
	 * finished elsewhere (a sibling tab, the server's own sweep) after this row lost track of it. Settle
	 * the row with the same lease-guarded terminal write a normal completion makes; the bytes are on the
	 * server, so there is nothing to retry and nothing to park.
	 */
	async function settleAlreadyCompleteAtCreate(
		session: Session,
		lease: UploadLease<Session>,
		error: unknown
	): Promise<"completed" | "yielded"> {
		const outcome = await runGuardedEffect(lease, session, async () => {
			if (session.ephemeral) return;
			await ports.sessionStore.markTerminalComplete(session);
			try {
				await ports.sessionStore.cleanupCompleted(session);
			} catch (cleanupError) {
				logCompletionCleanupFailure(session, cleanupError);
			}
		});
		try {
			ports.telemetry.logInfo("recording.fileUpload.alreadyCompleteAtCreate", {
				sessionId: session.id,
				uploadId: session.uploadId,
				registryId: isUploadAlreadyCompleteAtCreate(error) ? error.registryId : undefined,
				ownershipLost: outcome === "skipped-not-owned"
			});
		} catch {
			// Observability cannot invalidate a durable completion.
		}
		return outcome === "skipped-not-owned" ? "yielded" : "completed";
	}

	function isLifecycleParkedSession(session: Session): boolean {
		const persisted = session as Session & { status?: string; lastError?: string };
		return persisted.status === "needs-attention" && isUploadTerminalFailure(persisted.lastError);
	}

	async function parkLifecycleTerminal(
		session: Session,
		lease: UploadLease<Session>,
		error: unknown,
		trigger: "urls" | "parts" | "objectStatus" | "complete"
	): Promise<DriverResumeOutcome> {
		const alreadyParked = isLifecycleParkedSession(session);
		const normalized = ports.errors.normalize(error);
		const outcome = await runGuardedEffect(lease, session, () =>
			ports.sessionStore.markNeedsAttention(session, withFacadeErrorCodeInMessage(normalized.message, error), error)
		);
		if (outcome === "skipped-not-owned" || outcome === "already-cancelled") return "yielded";
		if (outcome === "already-complete") return "completed";
		if (outcome === "already-bytes-gone") return "bytes-gone";
		if (!alreadyParked) {
			try {
				ports.telemetry.logInfo("recording.fileUpload.terminalParked", {
					uploadId: session.uploadId, registryId: session.registryId, trigger: readUploadRefusalTrigger(error) ?? trigger,
					...readUploadTerminalDetails(error), rowAttemptId: session.attemptId,
					rowGeneration: session.generation, retryAttempt: session.retryAttempt ?? 0
				});
			} catch { /* Diagnostics cannot change a durable park. */ }
		}
		return "needs-attention";
	}

	async function releasePersistedSessionForRetry(
		session: Session,
		lease: UploadLease<Session>,
		failure?: {
			error: unknown;
			failureKind?: string;
			alreadyLogged?: boolean;
			slowTier?: boolean;
		}
	): Promise<DriverResumeOutcome> {
		if (failure && isUploadTerminalFailure(unwrapMultipartUrlRefreshError(failure.error))) {
			return parkLifecycleTerminal(session, lease, unwrapMultipartUrlRefreshError(failure.error), isMultipartUrlRefreshError(failure.error) ? "urls" : "parts");
		}
		const verificationPending = failure !== undefined && isUploadVerificationPending(failure.error);
		const verificationRetry = verificationPending ? verificationPendingRetryOptions(session, ports.now()) : undefined;
		const retryAt = verificationRetry?.retryAt ?? (failure?.slowTier ? computeSlowTierRetryAt({ attempt: session.retryAttempt ?? 0, nowMs: ports.now() }) : undefined);
		if (failure && !failure.alreadyLogged) {
			try {
				if (verificationPending) ports.telemetry.logInfo("recording.fileUpload.verificationPending", { sessionId: session.id, uploadId: session.uploadId, retryAt });
				else ports.telemetry.logError(
					"recording.fileUpload.resumeFailed",
					{
						sessionId: session.id,
						uploadId: session.uploadId,
						failureKind: failure.failureKind ?? "retry"
					},
					failure.error
				);
			} catch {
				// Failure telemetry must not replace the originating resume error or alter retry behavior.
			}
		}
		try {
			const outcome = await runGuardedEffect(lease, session, () =>
				verificationRetry
					? ports.sessionStore.releasePending(session, ports.errors.normalize(failure!.error).message, verificationRetry)
					: failure === undefined
					? ports.sessionStore.releasePending(session)
					: failure.slowTier
						? ports.sessionStore.releasePending(session, ports.errors.normalize(failure.error).message, {
								retryAt
							})
						: ports.sessionStore.releasePending(session, ports.errors.normalize(failure.error).message)
			);
			if (!verificationPending && outcome !== "skipped-not-owned" && retryAt !== undefined) {
				try {
					ports.telemetry.logInfo("recording.fileUpload.retrySlowReleased", { uploadId: session.uploadId, registryId: session.registryId, retryAt, retryAttempt: session.retryAttempt ?? 0 });
				} catch { /* Diagnostics cannot change a successful release. */ }
			}
			return outcome === "skipped-not-owned" ? "yielded" : "retry";
		} catch (pendingError) {
			try {
				ports.telemetry.logWarn(
					`[Upload] Could not mark "${session.fileName}" pending after persisted resume failure`,
					{
						sessionId: session.id,
						uploadId: session.uploadId,
						fileName: session.fileName
					},
					pendingError
				);
			} catch {
				// Pending-write diagnostics must never replace the originating resume failure.
			}
			if (verificationPending) throw pendingError;
			if (failure !== undefined) throw failure.error;
			throw pendingError;
		}
	}

	/**
	 * The chunked-object `/complete` refusal, handled identically wherever it arrives: re-mark the
	 * owed chunks under the writer fence, then release the row for retry with its own failure kind
	 * (and the slow tier once the row has already burned its diagnostic attempts).
	 */
	async function handlePartsIncompleteRefusal(input: {
		session: Session;
		error: unknown;
		lease: UploadLease<Session>;
		/**
		 * Both yields below mean "this tab stopped owning the row", but they fail at DIFFERENT steps
		 * and a future triage needs to tell them apart: `ownership-lost` means the lease was lost while
		 * re-marking the owed ordinals (the ledger write never applied), while `release-declined` means
		 * the re-mark DID apply and it was `releasePending`'s own CAS that declined. Reporting one
		 * reason for both made the two indistinguishable in telemetry.
		 */
		onYield?: (reason: "ownership-lost" | "release-declined") => void;
	}): Promise<DriverResumeOutcome> {
		const remark = await applyPartsIncompleteRemark({
			session: input.session,
			error: input.error,
			lease: input.lease,
			ports
		});
		if (remark.outcome === "skipped-not-owned") {
			input.onYield?.("ownership-lost");
			return "yielded";
		}
		// The refusal is not unconditionally retryable: `verifyListedParts` also refuses for reasons
		// re-PUTting the same bytes can never repair. Once this attempt has taken
		// MAX_UNPRODUCTIVE_PARTS_INCOMPLETE_REFUSALS refusals that re-marked nothing or repeated the
		// same reason, stop releasing the row for an identical next pass and surface it instead.
		const verdict = recordPartsIncompleteRefusal({
			session: input.session,
			error: input.error,
			chunkIndexes: remark.chunkIndexes,
			remarkApplied: remark.remarkApplied
		});
		if (verdict.exhausted) {
			input.onYield?.("release-declined");
			return escalateUnproductivePartsIncomplete({
				session: input.session,
				error: input.error,
				lease: input.lease,
				ports,
				verdict
			});
		}
		const released = await releasePersistedSessionForRetry(input.session, input.lease, {
			error: input.error,
			failureKind: PARTS_INCOMPLETE_FAILURE_KIND,
			slowTier: remark.slowTier
		});
		if (released === "yielded") input.onYield?.("release-declined");
		return released;
	}

	async function decidePersistedTerminal(
		session: Session,
		lease: UploadLease<Session>,
		evidence: PendingTerminalEvidence,
		cancelSignal?: AbortSignal
	): Promise<DriverResumeOutcome> {
		let listParts;
		try {
			listParts = await ports.multipartApi.listParts(session, {
				lease,
				ownsCurrentSession: () => lease.owns(session)
			});
		} catch (error) {
			if (isConfirmedMigrationIntegrityError(error)) {
				throw error;
			}
			return releasePersistedSessionForRetry(session, lease, {
				error,
				failureKind: "terminal-list-parts"
			});
		}
		if (cancelSignal?.aborted) {
			await runGuardedEffect(lease, session, () => ports.sessionStore.tombstoneCancelled(session)).catch((cleanupError) => {
				logResumeWarning(
					ports.telemetry,
					"recording.fileUpload.cleanupFailed",
					{
						sessionId: session.id,
						uploadId: session.uploadId,
						operation: "tombstone-cancelled-session"
					},
					cleanupError
				);
			});
			throw new UploadCancelledError(`Upload of "${session.fileName}" was cancelled`);
		}
		if (!(await lease.owns(session))) {
			ports.telemetry.logWarn("recording.fileUpload.yielded", {
				reason: "terminal-decision-lease-lost",
				sessionId: session.id,
				uploadId: session.uploadId
			});
			return "yielded";
		}
		const decision = await decideReconciliationTerminal({
			session,
			totalChunks: session.totalChunks,
			evidence: { ...evidence, listParts } as ReconciliationEvidence,
			policy: ports.policies.terminalRecovery,
			lease
		});
		if (decision.kind === "defer") {
			return releasePersistedSessionForRetry(session, lease, {
				error: decision.error,
				failureKind: "terminal-defer"
			});
		}
		return isRetryOutcome(decision.outcome) ? releasePersistedSessionForRetry(session, lease) : decision.outcome;
	}

	async function ensurePersistedSessionHasUploadTarget(
		session: Session,
		leaseScope: UploadLeaseScope<Session, LeaseToken>
	): Promise<{ kind: "ready"; session: Session } | { kind: "yielded" } | { kind: "completed" }> {
		if (
			session.endpointFamily === "uploads" &&
			typeof session.registryId === "string" &&
			typeof session.attemptId === "string" &&
			Number.isSafeInteger(session.generation) &&
			Array.isArray(parsePersistedChunkUrls(session.chunkUrlsJson)) &&
			parsePersistedChunkUrls(session.chunkUrlsJson).length === session.totalChunks
		) {
			return { kind: "ready", session };
		}
		const invalidTargetIdentityField = getInvalidMultipartTargetIdentityField(session);
		if (!invalidTargetIdentityField) {
			return { kind: "ready", session };
		}
		ports.telemetry.logWarn("recording.fileUpload.targetIdentityNotReady", {
			sessionId: session.id,
			uploadId: session.uploadId,
			invalidField: invalidTargetIdentityField
		});

		const verification = await verifyReinitializable(session);
		if (verification === "missing") {
			throw await createMissingBuffersError(session);
		}
		if (verification === "indeterminate") {
			throw new Error(`could not verify buffers for deferred upload session ${session.id}`);
		}
		if (!(await leaseScope.lease.owns(session))) {
			ports.telemetry.logWarn("recording.fileUpload.yielded", {
				reason: "target-init-not-owned",
				sessionId: session.id,
				uploadId: session.uploadId
			});
			return { kind: "yielded" };
		}
		if (!getInvalidMultipartTargetIdentityField(session)) {
			return { kind: "ready", session };
		}
		const persistedSession = await ports.sessionStore.getById(session.id);
		if (!(await leaseScope.lease.owns(session))) {
			ports.telemetry.logWarn("recording.fileUpload.yielded", {
				reason: "target-init-re-fetch-not-owned",
				sessionId: session.id,
				uploadId: session.uploadId
			});
			return { kind: "yielded" };
		}
		if (persistedSession && !getInvalidMultipartTargetIdentityField(persistedSession)) {
			return { kind: "ready", session: persistedSession };
		}

		await awaitPendingReservation(session.id);
		let target: Awaited<ReturnType<typeof ports.persistedTargetInitiator.initiatePersisted>>;
		try {
			target = await ports.persistedTargetInitiator.initiatePersisted({
				session,
				stableId: persistedSessionStableId(session),
				totalChunks: session.totalChunks
			});
		} catch (initiateError) {
			if (!isUploadAlreadyCompleteAtCreate(initiateError)) throw initiateError;
			const settled = await settleAlreadyCompleteAtCreate(session, leaseScope.lease, initiateError);
			return settled === "completed" ? { kind: "completed" } : { kind: "yielded" };
		}
		// initiatePersisted above just minted a REAL S3 multipart upload. Every exit from here that
		// isn't "attached" — a malformed response, the attach's CAS refusing/losing ownership, or the
		// attach itself throwing (asyncSessionStore's "session disappeared before/after target
		// initialization" throws by design) — leaves it referenced by nothing, orphaned on S3 forever,
		// unless explicitly rolled back here. Mirrors the identical lesson already applied to every
		// OTHER "mint a target this call may not get to keep" site in this subsystem (OptionsPage's
		// attachUploadTargetsToPersistedSessions/regenerateAbortedTargets, rounds 33/36/37;
		// targetInitiation.ts's own abortTargetsBestEffort) (round 38 / LSP-17735).
		const abortMintedTargetBestEffort = async (context: string): Promise<void> => {
			if (!ports.persistedTargetInitiator.abortPersisted) return;
			await ports.persistedTargetInitiator
				.abortPersisted(target, {
					preserveRecordingSession: session.fileCategory === "video"
				})
				.catch((abortError) => logBestEffortFailure(context, session, session.fileName, abortError));
		};
		try {
			assertValidMultipartTargetIdentity(target, "Persisted target initiation");
			// assertExactPresignedUrls (not the weaker assertExactNonEmptyUrls) — every OTHER
			// multipart-target mint site in this subsystem requires each chunkUrl to be a real
			// presigned http(s) URL (targetInitiation.ts, presignedUrl.ts's getFileUploadTargets/
			// getVideosUploadTargets/regenerateMultipartTarget). This mint site (and its two siblings
			// below) only checked non-blank strings, so a malformed relative/non-presigned response
			// still passed and got durably committed — the chunk PUT then resolves against the
			// capture app's own origin instead of S3 (round 46 / LSP-17735).
			assertExactPresignedUrls(uploadTargetChunkUrls(target), session.totalChunks, "Persisted target initiation");
		} catch (validationError) {
			await abortMintedTargetBestEffort("abort-persisted-target-invalid-response");
			throw validationError;
		}
		// Resolved from the JUST-MINTED target, after its validation gate — this call replaces the
		// session's previous target, so any adapter resolved earlier in the resume would be stale.
		// Deliberately below the try: `resolveUploadTargetAdapter` probes `family`, and probing it
		// above would move that (throwing) probe outside the abort-the-orphaned-mint guard.
		const targetAdapter = resolveUploadTargetAdapter(target);

		let attachedSession = session;
		let attachmentLostOwnership = false;
		try {
			const outcome = await runGuardedEffect(leaseScope.lease, session, async () => {
				const attachment = await ports.sessionStore.attachTarget(session, target, leaseScope.token);
				if (isTargetAttachmentResult(attachment)) {
					attachedSession = attachment.session;
					attachmentLostOwnership = attachment.kind === "skipped-not-owned";
				} else {
					attachedSession = attachment;
				}
				if (!(await leaseScope.lease.owns(session))) attachmentLostOwnership = true;
			});
			if (outcome === "skipped-not-owned" || attachmentLostOwnership) {
				ports.telemetry.logWarn("recording.fileUpload.yielded", {
					reason: "target-attachment-ownership-lost",
					sessionId: session.id,
					uploadId: session.uploadId
				});
				// Only abort the minted target when its OWN write never actually applied — attachTarget
				// can win its CAS (durably rewriting the row to point at this target) and ONLY THEN lose
				// ownership (the post-write owns() re-check above, rounds 32-36's fix, or a legacy
				// adapter's own attachment.kind), in which case the row now genuinely points at this
				// target and a successor drain will resume it. Aborting here would tear down the S3
				// multipart upload the row (and its successor) still needs, mirroring the identical
				// identity check reinitializeSession already uses to distinguish these two cases (round
				// 41 / LSP-17735) — an accumulation bug between rounds 32-36's post-attach ownership
				// re-check and round 38's abort-on-yield fix, each correct in isolation but never
				// checked against each other on this shared branch.
				if (attachedSession.uploadId !== targetAdapter.opaqueId(target)) {
					await abortMintedTargetBestEffort("abort-persisted-target-attach-yielded");
				}
				return { kind: "yielded" };
			}
			return { kind: "ready", session: attachedSession };
		} catch (attachError) {
			// Same identity gate as the yield branch above (round 41) — attachTarget can WIN its CAS
			// (durably rewriting the row, attachedSession assigned above) and only THEN have this
			// guarded effect throw, e.g. the post-write leaseScope.lease.owns(session) re-check itself
			// rejecting on a transient IndexedDB read failure (ownsFileUploadSessionLease is a bare,
			// non-transactional read). Aborting unconditionally on any throw would tear down the S3
			// multipart upload the row (and its successor drain) now genuinely needs, on an exit round
			// 41's fix never covered (round 44 / LSP-17735).
			if (attachedSession.uploadId !== targetAdapter.opaqueId(target)) {
				await abortMintedTargetBestEffort("abort-persisted-target-attach-threw");
			}
			throw attachError;
		}
	}

	async function createMissingBuffersError(session: Session): Promise<Error> {
		// TruncatedPartsError (not a bare Error), message text unchanged — locally-missing chunk
		// buffers are exactly as unfixable-by-retry as validatePersistedChunkByteIntegrity's
		// byte-total mismatch (round 51 / LSP-17735), but this throw site's error type had no branch
		// in either app's classifyFailure, so a deferred-target session reaching this path (e.g. via
		// ensurePersistedSessionHasUploadTarget) fell to the generic retryable "other" bucket and
		// span forever instead of reaching the terminal bytes-gone verdict the SIBLING throw site
		// (reinitializeSession, below) already reaches via recoverLostPersistedSession's
		// /buffers missing/i message match (round 52 / LSP-17735). Message text is unchanged so that
		// existing message-matching (chunkedUploadDriver.ts's own /buffers missing/i catch,
		// asyncSessionStore.ts's regex) keeps working unmodified.
		try {
			const chunks = await ports.sessionStore.listChunkMeta(session);
			let presentBufferCount = 0;
			for (const chunk of chunks) {
				const buffer = await ports.sessionStore.readChunkData(session, chunk.chunkIndex);
				if (buffer && buffer.byteLength > 0) presentBufferCount++;
			}
			if (presentBufferCount < chunks.length) {
				return new TruncatedPartsError(`buffers missing for ${session.id}: ${presentBufferCount}/${chunks.length}`);
			}
		} catch {
			// The semantic verification already proved bytes are missing. Preserve that
			// verdict even when the diagnostic count cannot be read a second time.
		}
		return new TruncatedPartsError(`buffers missing for ${session.id}`);
	}

	async function reinitializeSession(
		input: Parameters<ChunkedUploadDriver<FileLike, Session, LeaseToken>["reinitializeSession"]>[0]
	): Promise<Session> {
		const verification = await verifyReinitializable(input.session);
		if (verification === "missing") {
			throw await createMissingBuffersError(input.session);
		}
		if (verification === "indeterminate") {
			throw new Error(`could not verify buffers for reinitialization of ${input.session.id}`);
		}
		if (!(await input.lease.owns(input.session))) return input.session;

		await awaitPendingReservation(input.session.id);
		const target = await ports.persistedTargetInitiator.initiatePersisted({
			session: input.session,
			stableId: persistedSessionStableId(input.session),
			totalChunks: input.session.totalChunks
		});
		// Same "mint then might have to give it back" shape as ensurePersistedSessionHasUploadTarget
		// above (round 38 / LSP-17735) — every exit that isn't "the row now really points at this
		// target" leaves it orphaned on S3.
		const abortReinitializedTargetBestEffort = async (context: string): Promise<void> => {
			if (!ports.persistedTargetInitiator.abortPersisted) return;
			await ports.persistedTargetInitiator
				.abortPersisted(target, {
					preserveRecordingSession: input.session.fileCategory === "video"
				})
				.catch((abortError) => logBestEffortFailure(context, input.session, input.session.fileName, abortError));
		};
		try {
			assertValidMultipartTargetIdentity(target, "Reinitialized target");
			assertExactPresignedUrls(uploadTargetChunkUrls(target), input.session.totalChunks, "Reinitialized target");
		} catch (validationError) {
			await abortReinitializedTargetBestEffort("abort-reinitialized-target-invalid-response");
			throw validationError;
		}
		// Same rule as ensurePersistedSessionHasUploadTarget above: resolved from the just-minted
		// replacement target, below the try so the family probe stays inside the abort guard.
		const targetAdapter = resolveUploadTargetAdapter(target);

		let reinitializedSession = input.session;
		try {
			await runGuardedEffect(input.lease, input.session, async () => {
				reinitializedSession = await ports.sessionStore.resetForReinitialization(input.session, target);
			});
		} catch (resetError) {
			await abortReinitializedTargetBestEffort("abort-reinitialized-target-reset-threw");
			throw resetError;
		}
		if (reinitializedSession.uploadId !== targetAdapter.opaqueId(target)) {
			// The lease gate above can decline before the effect ever runs (outcome
			// "skipped-not-owned", reinitializedSession left as input.session), and
			// resetForReinitialization's own inner CAS (keyed on activeDrainId + the
			// PRE-reinitialization uploadId) can ALSO silently decline by returning the row
			// unchanged rather than throwing (asyncSessionStore.ts) — e.g. a concurrent drain
			// re-claimed the row between the lease check and the CAS's own read. Both cases are
			// detected here by the same signal: the returned session's uploadId never became the
			// just-minted target's, so the mint is orphaned either way.
			ports.telemetry.logWarn("recording.fileUpload.yielded", {
				reason: "reinitialization-write-declined",
				sessionId: input.session.id,
				uploadId: targetAdapter.opaqueId(target),
				currentUploadId: reinitializedSession.uploadId
			});
			await abortReinitializedTargetBestEffort("abort-reinitialized-target-write-declined");
		}
		return reinitializedSession;
	}

	async function recoverLostPersistedSession(input: {
		session: Session;
		error: unknown;
		lease: UploadLease<Session>;
	}): Promise<DriverResumeOutcome> {
		const completionOutcome = resolveCompletionOutcome({
			reconcile: "lost",
			verifyReinitializable: await verifyReinitializable(input.session),
			lostRecoveryPolicy: ports.policies.lostRecoveryPolicy
		});
		if (completionOutcome === "bytes-gone") {
			let reason = ports.errors.normalize(input.error).message;
			try {
				reason = (await createMissingBuffersError(input.session)).message;
			} catch {
				// Keep the original loss reason when the diagnostic probe is unavailable.
			}
			let outcome: Awaited<ReturnType<typeof runGuardedEffect>>;
			try {
				outcome = await runGuardedEffect(input.lease, input.session, () => ports.sessionStore.markBytesGone(input.session, reason));
			} catch (writeError) {
				try {
					ports.telemetry.logWarn(
						"recording.fileUpload.terminalYielded",
						{
							reason: "recover-lost-bytes-gone-write-refused",
							sessionId: input.session.id,
							uploadId: input.session.uploadId,
							s3Key: input.session.s3Key,
							evidenceKind: "recover-lost"
						},
						writeError
					);
				} catch {
					// A refused terminal verdict must not depend on telemetry availability.
				}
				throw writeError;
			}
			if (outcome === "skipped-not-owned") {
				ports.telemetry.logWarn("recording.fileUpload.terminalYielded", {
					reason: "recover-lost-bytes-gone-ownership-lost",
					sessionId: input.session.id,
					uploadId: input.session.uploadId
				});
				ports.telemetry.logWarn("recording.fileUpload.yielded", {
					reason: "recover-lost-bytes-gone-ownership-lost",
					sessionId: input.session.id,
					uploadId: input.session.uploadId
				});
				return "yielded";
			}
			try {
				ports.telemetry.logWarn(
					"recording.fileUpload.terminalBytesGone",
					{
						sessionId: input.session.id,
						uploadId: input.session.uploadId,
						s3Key: input.session.s3Key,
						evidenceKind: "recover-lost",
						reason
					},
					input.error
				);
			} catch {
				// A terminal data-loss verdict must not depend on telemetry availability.
			}
			return "bytes-gone";
		}
		if (completionOutcome === "defer") {
			await releasePersistedSessionForRetry(input.session, input.lease, {
				error: input.error,
				failureKind: "recovery-defer"
			});
			throw input.error;
		}
		if (completionOutcome === "fail-fast") throw input.error;

		try {
			await reinitializeSession({
				session: input.session,
				lease: input.lease
			});
		} catch (reinitializationError) {
			// LSP-22828: the upload this recovery would re-mint already finished on the server.
			if (isUploadAlreadyCompleteAtCreate(reinitializationError)) {
				return settleAlreadyCompleteAtCreate(input.session, input.lease, reinitializationError);
			}
			if (reinitializationError instanceof Error && /buffers missing/i.test(reinitializationError.message)) {
				await runGuardedEffect(input.lease, input.session, () =>
					ports.sessionStore.markBytesGone(input.session, reinitializationError.message)
				);
			}
			throw reinitializationError;
		}
		throw input.error;
	}

	async function finalizeMultipartCompletion(
		input: Parameters<ChunkedUploadDriver<FileLike, Session, LeaseToken>["finalizeMultipartCompletion"]>[0],
		guardPersistedEffects = false,
		cancelSignal?: AbortSignal
	): Promise<boolean> {
		const parts = [...input.parts].sort((left, right) => left.PartNumber - right.PartNumber);
		const ownershipLost = Symbol("ownership-lost-during-completion");
		let guardedCleanupHandled = false;
		try {
			const completed = await finalizeCompletionTransition({
				session: input.session,
				parts,
				totalChunks: input.session.totalChunks,
				ownsLease: (session) => input.lease.owns(session),
				onOwnershipLost: (phase, session) =>
					logResumeWarning(ports.telemetry, "recording.fileUpload.yielded", {
						reason: `completion-ownership-lost-${phase}`,
						sessionId: session.id,
						uploadId: session.uploadId
					}),
				completeMultipartUpload: async (completionParts) => {
					if (guardPersistedEffects && ports.policies.cancelEnabled && cancelSignal?.aborted) {
						throw new UploadCancelledError(`Upload of "${input.session.fileName}" was cancelled`);
					}
					const result = await resolveMultipartCompletionRequest({
						completeMultipartUpload: async () => {
							const completion = await ports.multipartApi.complete(input.session, completionParts);
							// A completion that landed ends this attempt's assembly-integrity streak
							// (finding 14) — the next failure starts a fresh count rather than inheriting
							// one from an upload that demonstrably works.
							clearAssemblyIntegrityFailures(input.session);
							return completion;
						},
						classifyCompletion: (error) => ports.errors.classifyCompletion(error),
						verifyReinitializable: () => verifyReinitializable(input.session),
						lostRecoveryPolicy: ports.policies.lostRecoveryPolicy,
						normalize: (error) => ports.errors.normalize(error)
					});
					if (result.alreadyComplete) {
						try {
							ports.telemetry.logInfo("recording.fileUpload.alreadyComplete", {
								sessionId: input.session.id,
								uploadId: input.session.uploadId
							});
						} catch {
							/* Observability cannot invalidate durable completion. */
						}
					}
				},
				markTerminalComplete: guardPersistedEffects
					? async (session) => {
							const outcome = await runGuardedEffect(input.lease, session, async () => {
								if (!session.ephemeral) {
									await ports.sessionStore.markTerminalComplete(session);
								}
								try {
									if (!session.ephemeral) {
										await ports.sessionStore.cleanupCompleted(session);
									}
								} catch (error) {
									logCompletionCleanupFailure(input.session, error);
								}
								guardedCleanupHandled = true;
							});
							if (outcome === "skipped-not-owned") throw ownershipLost;
						}
					: (session) => (session.ephemeral ? Promise.resolve() : ports.sessionStore.markTerminalComplete(session)),
				cleanupCompleted: guardPersistedEffects
					? async (session) => {
							if (!guardedCleanupHandled) {
								throw new Error(`Guarded cleanup was not handled for session ${session.id}`);
							}
						}
					: (session) => (session.ephemeral ? Promise.resolve() : ports.sessionStore.cleanupCompleted(session)),
				onCleanupError: (error) => logCompletionCleanupFailure(input.session, error)
			});
			return completed;
		} catch (error) {
			if (error === ownershipLost) return false;
			throw error;
		}
	}

	async function uploadFileDirect(
		input: ChunkedUploadFileInput<FileLike, LeaseToken>,
		totalChunks: number
	): Promise<DriverUploadOutcome> {
		// `input.target` is never reassigned in this function, and its sole caller (`uploadFile`)
		// has already probed the family twice — through `requiredChunksOrThrow` and its own
		// `opaqueId` — before this function is entered, so hoisting the probe to the top cannot
		// surface a throw any earlier than it already happens.
		const targetAdapter = resolveUploadTargetAdapter(input.target);
		const createSessionInput = (id: string, conversationId: string, ephemeral: boolean): NewSessionInput => ({
			id,
			batchId: input.batchId,
			uploadScope: input.uploadScope,
			ephemeral,
			conversationId,
			fileName: input.file.name,
			fileSize: input.file.size,
			// Prefer the caller's already-verified content-sniffed type over the FileLike's own
			// (spoofable) reported type — see persistUploadSession's identical rationale (round 75 /
			// LSP-17735). uploadFileDirect is the THIRD site that creates a durable, drainable session
			// row; missing this one left it re-mintable by the background drain from the spoofed
			// file.type/filename even after rounds 74/75 fixed the other two (round 77 / LSP-17735).
			fileType: input.detectedMimeType || input.file.type || "application/octet-stream",
			fileCategory: input.fileCategory ?? "video",
			target: input.target,
			totalChunks,
			cloudHosted: false,
			phase: "direct-upload",
			stableId: id,
			captureEvidence: input.captureEvidence ? { ...input.captureEvidence, sourceId: id } : undefined,
			detectedMimeType: input.detectedMimeType
		});

		let isPersistedSession = Boolean(input.conversationId);
		let session: Session | undefined;
		let leaseSession: Session | undefined;
		let mismatchedPersistedSession: Session | undefined;
		let terminalWriteAction: "mark-needs-attention" | "retain-lease" | "release-pending" | undefined;
		let freshIdentityFileBuffer: ArrayBuffer | undefined;
		let activityReporter: ReturnType<typeof createActivityReporter> | undefined;
		const cancelledBeforeUploadStarted = input.cancelSignal?.aborted === true;
		let sessionId = ports.createId();
		let sessionWasCreated = false;
		let leaseScope: DirectUploadLeaseScope<Session, LeaseToken> = isPersistedSession
			? ports.leaseCoordinator.createScope("direct-upload", sessionId, input.leaseToken)
			: createEphemeralUploadLeaseScope<Session>();
		const logYield = (reason: "claim-lost" | "re-fetch-mismatch" | "not-owned" | "ownership-lost" | "release-declined") => {
			ports.telemetry.logWarn("recording.fileUpload.yielded", {
				reason,
				sessionId: session?.id ?? sessionId,
				uploadId: session?.uploadId ?? targetAdapter.opaqueId(input.target)
			});
		};

		async function fallBackToEphemeralDirectSession(setupError: unknown): Promise<void> {
			if (ports.policies.directUploadQuotaPolicy === "fail") {
				throw Object.assign(
					new Error(
						`IndexedDB quota exceeded while preparing resumable upload for "${input.file.name}". Retry after freeing storage.`
					),
					{ cause: setupError }
				);
			}

			ports.telemetry.logWarn(
				`[Upload] "${input.file.name}" — IDB quota exceeded during upload setup, uploading without resume support`,
				{
					sessionId,
					uploadId: targetAdapter.opaqueId(input.target),
					fileName: input.file.name
				},
				setupError
			);
			isPersistedSession = false;
			sessionId = ports.createId();
			leaseScope = createEphemeralUploadLeaseScope<Session>();
			const fallbackSession = await createSession(createSessionInput(sessionId, "", true), leaseScope);
			session = fallbackSession.session;
			if (!fallbackSession.created || !matchesUploadIdentity(session, input.file, input.target)) {
				throw uploadIdentityMismatchError(session, input.file);
			}
			leaseSession = session;
		}

		try {
			session = isPersistedSession ? await ports.sessionStore.findByUploadId(targetAdapter.opaqueId(input.target)) : undefined;
			sessionId = session?.id ?? sessionId;
			leaseScope = isPersistedSession
				? ports.leaseCoordinator.createScope("direct-upload", sessionId, input.leaseToken)
				: createEphemeralUploadLeaseScope<Session>();
			if (!session) {
				const created = await createSession(
					createSessionInput(sessionId, input.conversationId ?? "", !isPersistedSession),
					leaseScope
				);
				session = created.session;
				if (!matchesUploadIdentity(session, input.file, input.target)) {
					if (created.created) {
						await runGuardedEffect(leaseScope.lease, session, () => ports.sessionStore.abandonSetup(session!)).catch(
							(cleanupError) =>
								logBestEffortFailure("abandon-mismatched-direct-session", session, input.file.name, cleanupError)
						);
					}
					throw uploadIdentityMismatchError(session, input.file);
				}
				sessionWasCreated = created.created;
			}
		} catch (setupError) {
			if (ports.errors.classifyStorage(setupError) !== "quota") {
				throw setupError;
			}
			await fallBackToEphemeralDirectSession(setupError);
			sessionWasCreated = true;
		}
		if (!sessionWasCreated) {
			if (!(await leaseScope.lease.claim(session!))) {
				logYield("claim-lost");
				return "yielded";
			}
			// claim() above just committed a durable "uploading" write. leaseScope.activate() has not
			// run yet at this point in the function (it runs much later, once the upload proper
			// starts), so leaseScope.release() below is a total no-op — the async ports'
			// generation-scoped release hits its "never-activated" guard and does nothing. Every exit
			// from here that gives up this claim must therefore durably demote it back to pending
			// itself, or the row is left "uploading" under a token nobody heartbeats, undrainable by
			// any tab for the full stale-lease window — the identical lesson already learned on the
			// sibling attach path (rounds 32/33/34 / LSP-17735) and on the identity-repair failure path
			// a few lines below in this same block, which already does exactly this before its own
			// leaseScope.release().
			// Captured the moment the claim above committed, so every demote below can tell "the claim
			// I am giving up is still mine" from "a NEWER claim has since taken this session over".
			// See UploadLeaseScope.captureClaimGeneration: the token alone cannot distinguish them,
			// because a re-claim of the same session legitimately REUSES the same token.
			const claimGenerationIsCurrent = leaseScope.captureClaimGeneration?.(session!);
			const demoteAbandonedDirectClaimBestEffort = async (identitySession: Session, context: string): Promise<void> => {
				// Deliberately NOT wrapped in runGuardedEffect's leaseScope.lease.owns() gate — on the
				// re-fetch-throw exit below, owns() (asyncUploadLease.ts -> ownsFileUploadSessionLease)
				// re-issues the SAME non-transactional db.fileUploadSessions.get() that just threw,
				// making the gate self-defeating: it rejects on exactly the failure it exists to
				// compensate, so runGuardedEffect's own promise rejects and the demote never runs at
				// all. The identical structural-dead-zone shape round 42 fixed on the sibling attach
				// path (round 43 / LSP-17735). Calling releasePending directly is safe by construction:
				// markFileUploadSessionPending (db.ts) does its own read INSIDE its transaction and
				// carries its own activeDrainId + uploadId + terminal-status CAS, so a row a genuine
				// successor has since re-claimed still declines there.
				//
				// ...unless that successor re-claimed the SAME session for the SAME uploadId (a retry
				// against the same target) reusing the SAME lease token — then BOTH CAS legs match the
				// live successor coincidentally and this stale demote yanks a row out from under a drain
				// that is actively uploading it. The generation captured at claim() time is the one
				// signal that separates the two, and is the same anti-staleness mechanism owns() uses
				// everywhere else in this flow (PR #3450 review / LSP-17735).
				if (claimGenerationIsCurrent && !claimGenerationIsCurrent()) {
					ports.telemetry.logWarn("recording.fileUpload.demoteSkipped", {
						reason: "claim-generation-superseded",
						context,
						sessionId: identitySession.id,
						uploadId: identitySession.uploadId
					});
					return;
				}
				await ports.sessionStore
					.releasePending(identitySession)
					.catch((cleanupError) => logBestEffortFailure(context, identitySession, input.file.name, cleanupError));
			};
			let claimedSession: Session | undefined;
			try {
				claimedSession = await ports.sessionStore.getById(session!.id);
			} catch (error) {
				// getById itself threw — no fresh read available, so reconstruct the identity claim()
				// just committed from the pre-claim session object (the same stale-identity fix round 32
				// established for the attach path: passing the pre-claim session's own activeDrainId
				// verbatim would make the CAS compare against the OLD value and silently decline).
				await demoteAbandonedDirectClaimBestEffort(
					{ ...session!, activeDrainId: leaseScope.token },
					"release-claimed-session-after-direct-upload-refetch-throw"
				);
				leaseScope.release(session!);
				throw error;
			}
			if (!claimedSession || claimedSession.uploadId !== targetAdapter.opaqueId(input.target)) {
				logYield("re-fetch-mismatch");
				// The fresh read supplies the row's CURRENT identity (notably its uploadId, which this
				// exit exists because it changed), but its activeDrainId is overridden with the token
				// claim() just committed — exactly as the throw branch above does. Passing the re-read
				// row verbatim made releasePending's CAS tautological: it compares the CURRENT row
				// against the identity it is handed, so a row a DIFFERENT, live drain has legitimately
				// re-claimed carries that drain's activeDrainId as both the actual and the expected
				// value, the CAS passes, and this tab demotes a claim that was never its own — yanking
				// the row out from under a drain that is actively uploading it. The only claim there is
				// to give up here is our own (LSP-17735).
				await demoteAbandonedDirectClaimBestEffort(
					{ ...(claimedSession ?? session!), activeDrainId: leaseScope.token },
					"release-claimed-session-after-direct-upload-refetch-mismatch"
				);
				leaseScope.release(session!);
				return "yielded";
			}
			if (!(await leaseScope.lease.owns(claimedSession))) {
				// Ownership may also be revoked by an auth change while this adapter still holds its
				// durable writer. Release only this scope's fenced claim; successor owners are unaffected.
				logYield("not-owned");
				leaseScope.release(claimedSession);
				return "yielded";
			}
			session = claimedSession;
			leaseSession = claimedSession;
			if (!matchesUploadIdentity(claimedSession, input.file, input.target)) {
				mismatchedPersistedSession = claimedSession;
				const mismatchError = uploadIdentityMismatchError(claimedSession, input.file);
				if (!claimedSession.cloudHosted && input.file.size <= ports.maxCloudArrayBufferBytes) {
					freshIdentityFileBuffer = await ports.chunkSources
						.readWholeFile(input.file, input.cancelSignal ?? new AbortController().signal)
						.catch((readError) => {
							logBestEffortFailure("read-whole-file-for-identity-repair", claimedSession, input.file.name, readError);
							return undefined;
						});
				}

				const freshSessionId = ports.createId();
				let freshSession: Awaited<ReturnType<typeof createSession>>;
				try {
					freshSession = await createSession(createSessionInput(freshSessionId, "", true), leaseScope);
				} catch (error) {
					await runGuardedEffect(leaseScope.lease, claimedSession, () => ports.sessionStore.releasePending(claimedSession)).catch(
						(cleanupError) =>
							logBestEffortFailure(
								"release-collided-session-after-identity-repair-failure",
								claimedSession,
								input.file.name,
								cleanupError
							)
					);
					leaseScope.release(claimedSession);
					throw error;
				}
				if (!freshSession.created || !matchesUploadIdentity(freshSession.session, input.file, input.target)) {
					// Sibling to the createSession-throw catch immediately above (identical claimedSession
					// still held from claim() at the top of this block) — that exit already demotes before
					// rethrowing; this one did not, safe only by omission (no real ports implementation
					// currently produces an uncreated/mismatched fresh session here) rather than by
					// construction (round 36 / LSP-17735).
					await runGuardedEffect(leaseScope.lease, claimedSession, () => ports.sessionStore.releasePending(claimedSession)).catch(
						(cleanupError) =>
							logBestEffortFailure(
								"release-collided-session-after-identity-repair-mismatch",
								claimedSession,
								input.file.name,
								cleanupError
							)
					);
					leaseScope.release(claimedSession);
					throw mismatchError;
				}
				session = freshSession.session;
				isPersistedSession = false;
				sessionWasCreated = true;
			}
		}
		leaseSession ??= session;
		const ownsCurrentSession = (): Promise<boolean> => leaseScope.lease.owns(leaseSession!);
		const directLease: UploadLease<Session> = {
			claim: () => leaseScope.lease.claim(leaseSession!),
			touch: () => leaseScope.lease.touch(leaseSession!),
			owns: ownsCurrentSession,
			ownerLiveness: () => leaseScope.lease.ownerLiveness(leaseSession!)
		};
		const markPersistedSetupFailureNeedsAttention = async (
			caughtError: Error,
			warningMessage: string
		): Promise<DriverUploadOutcome> => {
			terminalWriteAction = "mark-needs-attention";
			try {
				const outcome = await runGuardedEffect(directLease, session!, () =>
					ports.sessionStore.markNeedsAttention(session!, caughtError.message, caughtError)
				);
				if (outcome === "already-complete") return "completed";
				if (outcome === "already-cancelled" || outcome === "already-bytes-gone") return "yielded";
				if (outcome === "skipped-not-owned") {
					logYield("ownership-lost");
					return "yielded";
				}
			} catch (attentionError) {
				ports.telemetry.logWarn(
					warningMessage,
					{
						sessionId: session!.id,
						uploadId: session!.uploadId,
						fileName: input.file.name
					},
					attentionError
				);
			}
			throw caughtError;
		};
		if (sessionWasCreated && isPersistedSession) {
			let freshSessionEvidence: ListPartsEvidence["kind"] | "probe-failed" = "probe-failed";
			try {
				freshSessionEvidence = (
					await ports.multipartApi.listParts(session!, {
						lease: leaseScope.lease,
						ownsCurrentSession: () => leaseScope.lease.owns(leaseSession!)
					})
				).kind;
			} catch (probeError) {
				if (isConfirmedMigrationIntegrityError(probeError)) {
					try {
						return await markPersistedSetupFailureNeedsAttention(
							probeError,
							`[Upload] Could not persist multipart URL refresh failure for "${input.file.name}"`
						);
					} finally {
						leaseScope.release(leaseSession!);
					}
				}
				logResumeWarning(
					ports.telemetry,
					"recording.fileUpload.freshSessionProbeFailed",
					{ sessionId: session!.id, uploadId: session!.uploadId, s3Key: session!.s3Key },
					probeError
				);
			}
			if (freshSessionEvidence === "assembled") {
				if (!(await leaseScope.lease.owns(leaseSession!))) {
					logYield("ownership-lost");
					return "yielded";
				}
				if (!areTerminalEffectsAllowed(session!, "File upload fresh-session remote-complete probe")) {
					ports.telemetry.logWarn("recording.fileUpload.terminalYielded", {
						reason: "fresh-session-remote-complete-probe-runtime-policy-blocked",
						sessionId: session!.id,
						uploadId: session!.uploadId
					});
					leaseScope.release(leaseSession!);
					return "yielded";
				}
				try {
					ports.telemetry.logInfo("recording.fileUpload.alreadyComplete", {
						sessionId: session!.id,
						uploadId: session!.uploadId,
						evidenceKind: "fresh-session-probe"
					});
				} catch {
					// A durable terminal verdict must not depend on telemetry availability.
				}
				const terminalOutcome = await markPersistedSessionTerminalComplete({
					session: session!,
					ports,
					lease: leaseScope.lease
				});
				if (terminalOutcome === "yielded") {
					logYield("ownership-lost");
					leaseScope.release(leaseSession!);
					return "yielded";
				}
				leaseScope.release(leaseSession!);
				input.onProgress?.(session!.fileSize);
				return "completed";
			}
			// Silent-failure rule: record the non-terminal verdicts too, so a future "why did it
			// re-complete?" is one log line instead of a reconstruction from indirect signals.
			// Probe failures are already logged by the catch above.
			if (freshSessionEvidence !== "probe-failed") {
				try {
					ports.telemetry.logInfo("recording.fileUpload.freshSessionProbeContinued", {
						sessionId: session!.id,
						uploadId: session!.uploadId,
						evidenceKind: freshSessionEvidence
					});
				} catch {
					// Observability cannot gate the upload that follows.
				}
			}
		}

		let setupComplete = false;
		let parts: UploadedPart[] = [];
		let chunksCompleted = 0;
		let pendingChunkCount = totalChunks;
		let alreadyUploadedChunkCount = 0;
		const uploadStartedAt = ports.now();

		const runDirectSession = async (): Promise<"completed" | "yielded"> => {
			const allChunks =
				isPersistedSession && !sessionWasCreated
					? (await ports.sessionStore.listChunkMeta(session!))
							.filter(
								(chunk) => Number.isInteger(chunk.chunkIndex) && chunk.chunkIndex >= 0 && chunk.chunkIndex < totalChunks
							)
							.map((chunk) => ({
								...chunk,
								// A direct retry can skip a PUT only when the chunk can also seed a
								// valid multipart completion part. Damaged legacy markers without an
								// ETag must be re-PUT from the still-live File.
								isUploaded: chunk.isUploaded && isValidEtag(chunk.etag)
							}))
					: [];
			const existingChunkIndexes = new Set(allChunks.map((chunk) => chunk.chunkIndex));

			if (isPersistedSession) {
				// This is a resume of an already-adopted source. Re-probing the replacement handle
				// here would let a missing stream silently downgrade a durable stream session to
				// slicing/arrayBuffer and can lose bytes or materialize the whole file in memory.
				// Legacy rows are adopted once as slice by the session-store migration/defaulting path.
				const readStrategy = session!.sourceReadStrategy ?? "slice";
				if (readStrategy === "stream") {
					if (typeof (input.file as StreamCapableDriverFile).stream !== "function") {
						throw new PersistedSourceReadStrategyUnavailableError("stream", input.file.name);
					}
					try {
						await persistDriverFileStream(input.file as StreamCapableDriverFile, {
							partSize: CHUNK_SIZE,
							signal: input.cancelSignal,
							persistChunk: async (part) => {
								if (existingChunkIndexes.has(part.chunkIndex)) return;
								const persisted = await persistChunkBuffer({
									session: session!,
									chunkIndex: part.chunkIndex,
									buffer: part.buffer,
									ownsBeforePersist: sessionWasCreated ? undefined : ownsCurrentSession,
									ownsAfterPersist: sessionWasCreated ? undefined : ownsCurrentSession,
									onPersisted: (chunk) => allChunks.push(chunk)
								});
								if (!persisted) throw new StreamPersistenceOwnershipLostError();
							}
						});
					} catch (error) {
						if (error instanceof StreamPersistenceOwnershipLostError) return "yielded";
						throw error;
					}
				} else {
					const persistenceSource = ports.chunkSources.forPersistence(input.file);
					for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
						if (existingChunkIndexes.has(chunkIndex)) continue;
						const persisted = await persistChunkFromSource({
							session: session!,
							source: persistenceSource,
							chunkIndex,
							signal: input.cancelSignal ?? new AbortController().signal,
							onSourceFailure: (kind) => ports.errors.createChunkSourceError(kind, input.file.name),
							ownsBeforePersist: sessionWasCreated ? undefined : ownsCurrentSession,
							ownsAfterPersist: sessionWasCreated ? undefined : ownsCurrentSession,
							onPersisted: (chunk) => allChunks.push(chunk)
						});
						// Fresh-session persistence is already owner-token CAS guarded by the
						// store adapter. Reused sessions need this immediate post-write check;
						// fresh sessions are checked by the shared kernel before every PUT.
						if (!persisted) return "yielded";
					}
				}
			} else {
				for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
					const start = chunkIndex * CHUNK_SIZE;
					allChunks.push({
						chunkIndex,
						size: Math.min(start + CHUNK_SIZE, input.file.size) - start,
						isUploaded: false,
						etag: null
					});
				}
			}
			setupComplete = true;

			// Preference, not a contract: the server does not validate the order parts arrive in, so a
			// late moov (or any index the permutation does not name) is normal and merely finishes last.
			// `processWithAdaptiveConcurrency` dispatches strictly in input-array order, so this ordering
			// is what the chunk loop actually starts with. `orderPendingChunks` never drops or duplicates.
			const pendingChunks = orderPendingChunks(
				allChunks.filter((chunk) => !chunk.isUploaded),
				session!.priorityChunkIndexesJson
			);
			pendingChunkCount = pendingChunks.length;
			alreadyUploadedChunkCount = allChunks.length - pendingChunks.length;
			const preparedUrls = isPersistedSession
				? await preparePersistedUploadUrls({
						session: session!,
						pendingChunkIndexes: pendingChunks.map((chunk) => chunk.chunkIndex),
						fallbackUrls: targetAdapter.chunkUrls(input.target),
						ports,
						ownsCurrentSession,
						lease: leaseScope.lease
					})
				: await prepareDirectUploadUrls({
						session: session!,
						chunkUrls: targetAdapter.chunkUrls(input.target),
						chunkHeaders: targetAdapter.chunkHeaders(input.target),
						ports,
						ownsCurrentSession,
						lease: leaseScope.lease
					});
			const hasPreviouslyUploadedChunks = allChunks.some((chunk) => chunk.isUploaded);
			let progressReported = false;
			const reporter = createActivityReporter(session!, directLease);
			activityReporter = reporter;
			parts = [];
			const chunkLoopResult = await uploadPendingChunks({
				session: session!,
				allChunks,
				pendingChunks,
				chunkUrls: preparedUrls.chunkUrls,
				chunkHeaders: preparedUrls.chunkHeaders,
				parts,
				chunkSources: {
					forPersistedUpload: () =>
						freshIdentityFileBuffer
							? {
									async read(chunkIndex) {
										const start = chunkIndex * CHUNK_SIZE;
										const buffer = freshIdentityFileBuffer!.slice(start, Math.min(start + CHUNK_SIZE, input.file.size));
										return buffer.byteLength > 0
											? { ok: true as const, blob: new Blob([buffer]) }
											: { ok: false as const, kind: "missing" as const };
									}
								}
							: ports.chunkSources.forDirectUpload({
									file: input.file,
									session: isPersistedSession ? session : undefined,
									cloudHosted: session!.cloudHosted,
									chunkSize: CHUNK_SIZE
								})
				},
				transport: ports.chunkTransport,
				lease: directLease,
				telemetry: ports.telemetry,
				chunkSize: CHUNK_SIZE,
				chunkConcurrency: ports.chunkConcurrency,
				maxChunkRetries: ports.maxChunkRetries,
				cancelSignal: input.cancelSignal,
				refreshPresignedUrl: preparedUrls.refreshPresignedUrl,
				checkpointChunk: (chunkIndex, etag) =>
					isPersistedSession ? ports.sessionStore.markChunkUploaded(session!, chunkIndex, etag) : Promise.resolve(),
				// LSP-19201: the direct path can lose a receipt to the same middlebox as the resume path, and the
				// recovery is just as valid here — the part is on S3 either way. See partReceiptRecovery.ts.
				recoverPartReceipt: createPartReceiptRecovery({
 partReceipt: session!.contractVersion === 2 && ports.multipartApi.partReceipt ? ordinal => ports.multipartApi.partReceipt!(session!, ordinal) : undefined,
					listParts: () =>
						ports.multipartApi.listParts(session!, {
							lease: leaseScope.lease,
							ownsCurrentSession
						}),
					telemetry: ports.telemetry,
					context: () => ({
						sessionId: session!.id,
						conversationId: session!.conversationId,
						uploadId: session!.uploadId,
						fileName: input.file.name
					})
				}),
				createChunkSourceError: (kind, fileName) => ports.errors.createChunkSourceError(kind, fileName),
				onCheckpointError: (error, chunkIndex) =>
					ports.telemetry.logError(
						"recording.fileUpload.directCheckpointFailed",
						{
							chunkIndex,
							sessionId: session!.id,
							uploadId: session!.uploadId,
							fileName: input.file.name
						},
						error
					),
				onLeaseTouchError: (error, chunkIndex) =>
					ports.telemetry.logWarn(
						"recording.fileUpload.directLeaseTouchFailed",
						{
							chunkIndex,
							sessionId: session!.id,
							uploadId: session!.uploadId,
							fileName: input.file.name
						},
						error
					),
				onChunkComplete: ({ chunkIndex, chunkSize, chunksCompleted: completedChunks, totalPendingChunks }) => {
					chunksCompleted = completedChunks;
					reporter.signal();
					ports.telemetry.logInfo(`[Upload] "${input.file.name}" part ${chunkIndex + 1}/${totalChunks} complete`, {
						chunkSize,
						completedChunks,
						totalPendingChunks
					});
				},
				onProgress: (bytesUploaded) => {
					if (!hasPreviouslyUploadedChunks && !progressReported && bytesUploaded === 0) {
						return;
					}
					progressReported = true;
					input.onProgress(bytesUploaded);
				},
				onActivity: () => reporter.signal(),
				isOnline: ports.connectivity?.isOnline,
				waitForOnline: ports.connectivity?.waitForOnline,
				isPresignedUrlExpired: ports.isPresignedUrlExpired,
				retryDelay: ports.retryDelay,
				ensureOwnershipAfterRead: true
			});
			if (chunkLoopResult.outcome === "yielded") return "yielded";

			if (!hasValidMultipartParts(parts, totalChunks)) {
				throw (
					ports.persistedResume.createNonContiguousPartsError?.({
						session: session!,
						uploadedPartCount: parts.length
					}) ??
					new Error(
						`Upload of "${input.file.name}" aborted before completion: have ${parts.length}/${totalChunks} part etags (expected contiguous 1..${totalChunks}). Refusing to complete a truncated object; upload stays recoverable.`
					)
				);
			}
			if (ports.policies.cancelEnabled && input.cancelSignal?.aborted) {
				throw new UploadCancelledError(`Upload of "${input.file.name}" was cancelled`);
			}
			const completed = await finalizeMultipartCompletion({ session: session!, parts, lease: directLease }, true, input.cancelSignal);
			return completed ? "completed" : "yielded";
		};

		const runWithCurrentSession = () => leaseScope.runWithLiveness(leaseSession!, runDirectSession);

		try {
			// Activated as the first statement inside this try (not earlier, at function-body
			// scope) so that ANY throw between activation and the actual upload work — even a
			// synchronous one from something as innocuous-looking as ports.now() — still reaches
			// the release() in this try's finally below, instead of leaking this session's Map
			// entry/heartbeat with nothing left to ever release it.
			leaseScope.activate(leaseSession!);
			let outcome: "completed" | "yielded";
			try {
				outcome = await runWithCurrentSession();
			} catch (setupError) {
				const storageFailure = ports.errors.classifyStorage(setupError);
				if (!setupComplete && storageFailure === "quota") {
					if (sessionWasCreated) {
						try {
							const abandonOutcome = await runGuardedEffect(directLease, session!, () =>
								ports.sessionStore.abandonSetup(session!)
							);
							if (abandonOutcome === "skipped-not-owned") {
								logYield("not-owned");
								return "yielded";
							}
						} catch (cleanupError) {
							logBestEffortFailure("abandon-quota-fallback-session", session, input.file.name, cleanupError);
						}
					} else if (!(await directLease.owns(session!))) {
						logYield("not-owned");
						return "yielded";
					}
					leaseScope.release(leaseSession!);
					await fallBackToEphemeralDirectSession(setupError);
					leaseScope.activate(leaseSession!);
					setupComplete = false;
					outcome = await runWithCurrentSession();
				} else {
					throw setupError;
				}
			}
			if (outcome === "yielded") {
				logYield("ownership-lost");
				return "yielded";
			}
			const durationSeconds = ((ports.now() - uploadStartedAt) / 1000).toFixed(1);
			ports.telemetry.logInfo(`[Upload] "${input.file.name}" completed in ${durationSeconds}s`, {
				fileName: input.file.name,
				sessionId: isPersistedSession ? session!.id : undefined,
				conversationId: input.conversationId,
				durationSeconds,
				uploadedChunks: totalChunks,
				resumedChunks: alreadyUploadedChunkCount,
				totalChunks
			});
			return "completed";
		} catch (caughtError) {
			if (caughtError instanceof UploadOwnershipLostError) {
				logYield("ownership-lost");
				return "yielded";
			}
			if (caughtError instanceof PersistedSourceReadStrategyUnavailableError && isPersistedSession) {
				// Do not delete persisted chunks or tombstone the session. The original source bytes
				// remain recoverable if a compatible source is supplied later.
				return await markPersistedSetupFailureNeedsAttention(
					caughtError,
					`[Upload] Could not persist unavailable source strategy for "${input.file.name}"`
				);
			}
			if (isConfirmedMigrationIntegrityError(caughtError) && isPersistedSession && !setupComplete) {
				return await markPersistedSetupFailureNeedsAttention(
					caughtError,
					`[Upload] Could not persist multipart URL refresh failure for "${input.file.name}"`
				);
			}
			if (!setupComplete) {
				if (caughtError instanceof FileUnreadableError && isPersistedSession) {
					await runGuardedEffect(directLease, session!, async () => {
						await ports.sessionStore
							.deleteAllChunkData(session!)
							.catch((cleanupError) =>
								logBestEffortFailure("delete-unreadable-direct-setup-chunks", session, input.file.name, cleanupError)
							);
						await ports.sessionStore.tombstoneCancelled(session!, {
							notifyOtherTabs: false
						});
					}).catch((cleanupError) =>
						logBestEffortFailure("tombstone-unreadable-direct-setup-session", session, input.file.name, cleanupError)
					);
				}
				leaseScope.release(leaseSession!);
				throw caughtError;
			}
			if (caughtError instanceof UploadCancelledError) {
				if (isPersistedSession && ports.policies.cancelEnabled) {
					await runGuardedEffect(directLease, session!, () => ports.sessionStore.tombstoneCancelled(session!)).catch(
						(cleanupError) => logBestEffortFailure("tombstone-cancelled-direct-session", session, input.file.name, cleanupError)
					);
				}
				throw caughtError;
			}
			if (caughtError instanceof FileUnreadableError) {
				if (isPersistedSession) {
					await runGuardedEffect(directLease, session!, async () => {
						await ports.sessionStore
							.deleteAllChunkData(session!)
							.catch((cleanupError) =>
								logBestEffortFailure("delete-unreadable-direct-chunks", session, input.file.name, cleanupError)
							);
						await ports.sessionStore.tombstoneCancelled(session!, {
							notifyOtherTabs: false
						});
					}).catch((cleanupError) =>
						logBestEffortFailure("tombstone-unreadable-direct-session", session, input.file.name, cleanupError)
					);
				}
				throw caughtError;
			}

			let effectiveError = caughtError;
			if (caughtError instanceof MultipartUploadGoneError && recoversGoneMultipartByProbe(ports.policies)) {
				try {
					const recoveryOutcome = await recoverGoneMultipartForFailFastPolicy({
						session: session!,
						ports,
						lease: directLease,
						parts,
						// A validated direct request still owns its live file, even when
						// persisted metadata contains uploaded chunks without buffers.
						verifyReinitializable: async () => "guaranteed"
					});
					if (recoveryOutcome === "yielded") logYield("ownership-lost");
					return recoveryOutcome;
				} catch (recoveryError) {
					effectiveError = recoveryError;
				}
			}

			if (isPersistedSession && isUploadVerificationPending(effectiveError)) {
				if (ports.policies.cancelEnabled && input.cancelSignal?.aborted)
					throw new UploadCancelledError(`Upload of "${input.file.name}" was cancelled`);
				const retry = verificationPendingRetryOptions(session!, ports.now());
				let released: Awaited<ReturnType<typeof runGuardedEffect>> | undefined;
				try {
					released = await runGuardedEffect(directLease, session!, () =>
						ports.sessionStore.releasePending(session!, ports.errors.normalize(effectiveError).message, retry));
				} catch (pendingWriteError) {
					// A persistence failure is a real fault, even when verification itself is healthy.
					effectiveError = pendingWriteError;
				}
				if (released !== undefined) {
					if (ports.policies.cancelEnabled && input.cancelSignal?.aborted)
						throw new UploadCancelledError(`Upload of "${input.file.name}" was cancelled`);
					if (released === "skipped-not-owned") return "yielded";
					try { ports.telemetry.logInfo("recording.fileUpload.verificationPending", { sessionId: session!.id, uploadId: session!.uploadId, retryAt: retry.retryAt }); }
					catch { /* Observability cannot change the pending source. */ }
					throw effectiveError;
				}
			}

			if (isPersistedSession && isUploadPartsIncompleteError(effectiveError)) {
				// The FRESH upload reaches its completion through here, so the recovery has to live on
				// this path too — the generic classifier below would release the row with the ledger
				// still claiming every chunk landed, and the next pass would re-complete the identical
				// incomplete manifest.
				terminalWriteAction = "release-pending";
				try {
					await handlePartsIncompleteRefusal({
						session: session!,
						error: effectiveError,
						lease: directLease,
						onYield: logYield
					});
					// Not rethrown: the row is pending with the owed chunks re-marked, so the drain's
					// next pass re-uploads them rather than announcing a dead upload.
					return "yielded";
				} catch (partsIncompleteWriteError) {
					// The durable write failed; fall through to the generic handling below so the
					// failure is still recorded and surfaced exactly as it was before this branch.
					logBestEffortFailure("remark-parts-incomplete-direct-session", session, input.file.name, partsIncompleteWriteError);
				}
			}

			if (isPersistedSession) {
				const classification = ports.errors.classifyCompletion(effectiveError);
				if (!(classification === "lost" && ports.policies.lostRecoveryPolicy === "fail-fast")) {
					terminalWriteAction = "release-pending";
					try {
						const failureKind = isConfirmedMigrationIntegrityError(effectiveError) || isUploadControlUnavailable(unwrapMultipartUrlRefreshError(effectiveError))
							? "needs-attention"
							: ports.persistedResume.classifyFailure(unwrapMultipartUrlRefreshError(effectiveError));
						if (isUploadTerminalFailure(unwrapMultipartUrlRefreshError(effectiveError))) {
							terminalWriteAction = "mark-needs-attention";
							const originalError = unwrapMultipartUrlRefreshError(effectiveError);
							const outcome = await parkLifecycleTerminal(session!, directLease, originalError, isMultipartUrlRefreshError(effectiveError) ? "urls" : "complete");
							effectiveError = originalError;
							if (outcome === "completed") return "completed";
							if (outcome === "yielded" || outcome === "bytes-gone") return "yielded";
						} else if (failureKind === "needs-attention") {
							terminalWriteAction = "mark-needs-attention";
							const outcome = await runGuardedEffect(directLease, session!, () =>
								ports.sessionStore.markNeedsAttention(
									session!,
									ports.errors.normalize(effectiveError).message,
									effectiveError
								)
							);
							if (outcome === "already-complete") return "completed";
							if (outcome === "already-cancelled") return "yielded";
							if (outcome === "skipped-not-owned") {
								logYield("ownership-lost");
								return "yielded";
							}
							if (outcome === "already-bytes-gone") return "yielded";
						} else if (
							input.releaseActiveOnFinally === false &&
							classifyDirectUploadFailureKind(unwrapMultipartUrlRefreshError(effectiveError)) === "network"
						) {
							// The caller is retaining this foreground session across its retry
							// pause after a network failure — keep the durable lease too because
							// the same target remains retryable. HTTP failures release to pending
							// below so the foreground caller can replace the rejected/expired target.
							terminalWriteAction = "retain-lease";
							try {
								await directLease.touch(session!);
							} catch (leaseTouchError) {
								if (isUploadLeaseLostError(leaseTouchError)) {
									leaseScope.release(session!);
								}
								throw leaseTouchError;
							}
							if (!(await directLease.owns(session!))) {
								// The lease was already stolen before this touch — the caller still
								// believes it owns the session (in-memory flag + live heartbeat) while
								// the durable row belongs to someone else. Surface it; there is nothing
								// left for this call to do about it.
								ports.telemetry.logWarn(
									`[Upload] Retained lease for "${input.file.name}" was already lost to another owner`,
									{
										sessionId: session!.id,
										uploadId: session!.uploadId,
										fileName: input.file.name
									},
									effectiveError
								);
								leaseScope.release(session!);
							} else {
								await ports.sessionStore.recordFailure?.(session!, ports.errors.normalize(effectiveError).message);
							}
						} else {
							await runGuardedEffect(directLease, session!, () =>
								failureKind === "retry-slow"
									? ports.sessionStore.releasePending(session!, ports.errors.normalize(effectiveError).message, {
											retryAt: computeSlowTierRetryAt({
												attempt: session!.retryAttempt ?? 0,
												nowMs: ports.now()
											})
										})
									: ports.sessionStore.releasePending(session!, ports.errors.normalize(effectiveError).message)
							);
						}
					} catch (terminalWriteError) {
						ports.telemetry.logWarn(
							`[Upload] Could not persist failure state for "${input.file.name}" (action: ${terminalWriteAction}); preserving original error`,
							{
								sessionId: session!.id,
								uploadId: session!.uploadId,
								fileName: input.file.name,
								terminalWriteAction
							},
							terminalWriteError
						);
						if (
							classification === "transient" &&
							effectiveError instanceof Error &&
							"cause" in effectiveError &&
							effectiveError.cause !== undefined
						) {
							effectiveError = effectiveError.cause;
						}
					}
				}
			}
			const normalizedError = ports.errors.normalize(effectiveError);
			const durationSeconds = ((ports.now() - uploadStartedAt) / 1000).toFixed(1);
			ports.telemetry.logError(
				"recording.fileUpload.failed",
				{
					fileName: input.file.name,
					sessionId: isPersistedSession ? session!.id : undefined,
					uploadId: session?.uploadId ?? targetAdapter.opaqueId(input.target),
					conversationId: input.conversationId,
					phase: chunksCompleted === pendingChunkCount ? "multipart completion" : "chunk upload",
					failureKind: classifyDirectUploadFailureKind(effectiveError),
					httpStatus: directUploadHttpStatus(normalizedError),
					durationSeconds,
					uploadedChunks: chunksCompleted + alreadyUploadedChunkCount,
					totalChunks,
					terminalWriteAction
				},
				normalizedError
			);
			throw effectiveError;
		} finally {
			activityReporter?.stop();
			if (mismatchedPersistedSession && !cancelledBeforeUploadStarted) {
				await runGuardedEffect(directLease, mismatchedPersistedSession, () =>
					ports.sessionStore.releasePending(mismatchedPersistedSession!)
				).catch((cleanupError) => {
					try {
						ports.telemetry.logWarn(
							`[Upload] Could not release collided session ${mismatchedPersistedSession!.id} after isolating file identity`,
							{
								sessionId: mismatchedPersistedSession!.id,
								uploadId: mismatchedPersistedSession!.uploadId,
								fileName: mismatchedPersistedSession!.fileName
							},
							cleanupError
						);
					} catch {
						// Cleanup telemetry must not replace a successful isolated upload.
					}
				});
			}
			if (terminalWriteAction !== "retain-lease" || input.releaseActiveOnFinally !== false) {
				leaseScope.release(leaseSession!);
			}
		}
	}

	async function resumePersistedMultipartSession(
		input: Parameters<ChunkedUploadDriver<FileLike, Session, LeaseToken>["resumePersistedMultipartSession"]>[0]
	): Promise<DriverResumeOutcome> {
		if (isLifecycleParkedSession(input.session)) return "needs-attention";
		return controlFlow.runResumeWithCancellation(input.session.id, input.cancelSignal, async (resumeAttempt) => {
			const leaseScope = input.leaseScope ?? ports.leaseCoordinator.createScope("resume", input.session.id);
			let session = input.session;
			const forceCompletionReconciliationProbe = [...SETTLING_FACADE_ERROR_CODES].some((code) =>
				hasPersistedUploadFacadeErrorCode((input.session as Session & { lastError?: unknown }).lastError, code)
			);
			let activityReporter: ReturnType<typeof createActivityReporter> | undefined;
			const logResumeYield = (reason: string) =>
				logResumeWarning(ports.telemetry, "recording.fileUpload.yielded", {
					reason,
					sessionId: session.id,
					uploadId: session.uploadId
				});
			const throwIfUserCancelled = async (): Promise<void> => {
				if (!input.cancelSignal?.aborted) return;
				await runGuardedEffect(leaseScope.lease, session, () => ports.sessionStore.tombstoneCancelled(session)).catch(
					(cleanupError) => {
						logResumeWarning(
							ports.telemetry,
							"recording.fileUpload.cleanupFailed",
							{
								sessionId: session.id,
								uploadId: session.uploadId,
								operation: "tombstone-cancelled-session"
							},
							cleanupError
						);
					}
				);
				throw new UploadCancelledError(`Upload of "${session.fileName}" was cancelled`);
			};
			if (!(await leaseScope.lease.claim(session))) {
				logResumeYield("resume-claim-lost");
				return "yielded";
			}
			leaseScope.activate(session);

			const runResume = async (): Promise<DriverResumeOutcome> => {
				try {
					// Before the reconciliation probe below: a row whose own chunk count is unusable must
					// never have a ListParts listing read as "the server already holds the whole object".
					assertUsablePersistedTotalChunks(session, ports.telemetry);
					const targetEnsure = await ensurePersistedSessionHasUploadTarget(session, leaseScope);
					if (targetEnsure.kind === "yielded") return "yielded";
					if (targetEnsure.kind === "completed") return "completed";
					session = targetEnsure.session;
					const reporter = createActivityReporter(session, leaseScope.lease);
					activityReporter = reporter;
					const reconciliation = await reconcilePersistedSession({
						session,
						store: ports.sessionStore,
						lease: leaseScope.lease,
						probe: {
							listParts: (currentSession) =>
								ports.multipartApi.listParts(currentSession, {
									lease: leaseScope.lease,
									ownsCurrentSession: () => leaseScope.lease.owns(currentSession)
								})
						},
						policy: {
							async decide(context) {
								if (input.cancelSignal?.aborted) {
									await runGuardedEffect(leaseScope.lease, context.session, () =>
										ports.sessionStore.tombstoneCancelled(context.session)
									).catch((cleanupError) => {
										logResumeWarning(
											ports.telemetry,
											"recording.fileUpload.cleanupFailed",
											{
												sessionId: context.session.id,
												uploadId: context.session.uploadId,
												operation: "tombstone-cancelled-session"
											},
											cleanupError
										);
									});
									throw new UploadCancelledError(`Upload of "${context.session.fileName}" was cancelled`);
								}
								if (resumeAttempt.signal.aborted || !(await leaseScope.lease.owns(context.session))) {
									logResumeYield("reconciliation-terminal-decision-lease-lost");
									return "yielded";
								}
								return ports.policies.terminalRecovery.decide(context, leaseScope.lease);
							}
						},
						meta: {
							chunkIndex: (chunk) => chunk.chunkIndex,
							isUploaded: (chunk) => chunk.isUploaded,
							etag: (chunk) => chunk.etag,
							adoptEtag: (chunk, etag) => ({
								...chunk,
								isUploaded: true,
								etag
							}),
							createFromServerPart: (currentSession, chunkIndex, etag, size) => ({
								sessionId: currentSession.id,
								chunkIndex,
								size,
								isUploaded: true,
								etag
							})
						},
						classifyData: ports.persistedResume.classifyData,
						reconcileReadFailure: ports.persistedResume.reconcileReadFailure,
						forceProbe: forceCompletionReconciliationProbe
					});

					if (reconciliation.kind === "lease-lost") {
						logResumeYield("reconciliation-lease-lost");
						return "yielded";
					}
					if (reconciliation.kind === "terminal") {
						return isRetryOutcome(reconciliation.outcome)
							? releasePersistedSessionForRetry(session, leaseScope.lease)
							: reconciliation.outcome;
					}
					if (reconciliation.kind === "defer") {
						if (isMultipartUrlRefreshError(reconciliation.error)) throw reconciliation.error;
						if (ports.persistedResume.reconciliationDefer === "retry") {
							return releasePersistedSessionForRetry(session, leaseScope.lease, {
								error: reconciliation.error,
								failureKind: "reconciliation-defer"
							});
						}
						throw reconciliation.error;
					}

					const { allChunkMeta, unuploadedMeta, probedListParts } = reconciliation;
					if (unuploadedMeta.length === 0) {
						validatePersistedChunkByteIntegrity(session, allChunkMeta);
						const parts = allChunkMeta
							.filter((chunk): chunk is DriverChunkMeta & { etag: string } => chunk.isUploaded && isValidEtag(chunk.etag))
							.map((chunk) => ({
								ETag: chunk.etag,
								PartNumber: chunk.chunkIndex + 1
							}))
							.sort((left, right) => left.PartNumber - right.PartNumber);
						if (!hasValidMultipartParts(parts, session.totalChunks)) {
							if (ports.persistedResume.createNonContiguousPartsError) {
								throw ports.persistedResume.createNonContiguousPartsError({
									session,
									uploadedPartCount: parts.length
								});
							}
							return decidePersistedTerminal(
								session,
								leaseScope.lease,
								{
									kind: "local-gaps",
									missingBufferIndexes: [],
									missingMetadataCount: Math.max(1, session.totalChunks - parts.length)
								},
								input.cancelSignal
							);
						}
						input.onProgress?.(
							createUploadedChunkBytes(allChunkMeta, session.totalChunks).reduce(
								(totalBytes, chunkBytes) => totalBytes + chunkBytes,
								0
							)
						);
						const lastUploadedChunk = allChunkMeta[allChunkMeta.length - 1];
						input.onChunkComplete?.({
							chunkIndex: lastUploadedChunk.chunkIndex,
							chunkSize: lastUploadedChunk.size,
							chunksCompleted: 0,
							totalPendingChunks: 0
						});
						// REMOTE-COMPLETE PROBE (resume only).
						//
						// Reaching here means a RESUMED session's local chunk grid is fully intact, so
						// `reconcilePersistedSession` returned `ready` WITHOUT ever asking the server what it
						// holds — it probes ListParts only when a chunk buffer or a metadata row is missing.
						// When the server has meanwhile assembled the object (a sweep finished the stranded
						// MPU, or an earlier completion landed and its response was lost), the completion
						// below is re-sent identically on every drain pass against an upload that is done.
						//
						// One ListParts call answers that. `assembled` is the server's own positive verdict
						// ("sealed/ready — there is no open MPU left to complete"), so the only correct action
						// is to retire the row. `verified` (MPU still open) and `gone` (never proof of loss —
						// see the ListPartsEvidence docs) both fall through to the normal completion, which
						// already owns those cases. Fresh, non-resumed uploads never reach this code path, so
						// they stay zero-overhead.
						//
						// A probe FAILURE must never block a completion that would otherwise succeed, so it
						// is logged and the normal path continues.
						//
						// SKIPPED when the reconciliation itself already probed (`probedListParts`): the
						// post-adoption path consulted the server moments ago and its `assembled` case was
						// already the terminal policy's to decide, so a second ListParts here is pure
						// redundancy — and it double-consumed single-shot ListParts harnesses
						// (breakit-f.batch2-terminal-yield.spec.ts hung on the second, never-resolving call).
						let remoteCompleteEvidence: "assembled" | "verified" | "gone" | "probe-failed" | "probe-skipped" = "probe-skipped";
						if (!probedListParts) {
							try {
								remoteCompleteEvidence = (
									await ports.multipartApi.listParts(session, {
										lease: leaseScope.lease,
										ownsCurrentSession: () => leaseScope.lease.owns(session)
									})
								).kind;
							} catch (probeError) {
								if (isConfirmedMigrationIntegrityError(probeError)) {
									throw probeError;
								}
								remoteCompleteEvidence = "probe-failed";
								logResumeWarning(
									ports.telemetry,
									"recording.fileUpload.remoteCompleteProbeFailed",
									{
										sessionId: session.id,
										uploadId: session.uploadId,
										s3Key: session.s3Key
									},
									probeError
								);
							}
						}
						if (remoteCompleteEvidence === "assembled") {
							if (!(await leaseScope.lease.owns(session))) {
								logResumeYield("remote-complete-probe-ownership-lost");
								return "yielded";
							}
							if (!areTerminalEffectsAllowed(session, "File upload resume remote-complete probe")) {
								logResumeWarning(ports.telemetry, "recording.fileUpload.terminalYielded", {
									reason: "resume-remote-complete-probe-runtime-policy-blocked",
									sessionId: session.id,
									uploadId: session.uploadId
								});
								return "yielded";
							}
							try {
								ports.telemetry.logInfo("recording.fileUpload.alreadyComplete", {
									sessionId: session.id,
									uploadId: session.uploadId,
									evidenceKind: "resume-remote-complete-probe"
								});
							} catch {
								// A durable terminal verdict must not depend on telemetry availability.
							}
							const remoteCompleteOutcome = await markPersistedSessionTerminalComplete({
								session,
								ports,
								lease: leaseScope.lease
							});
							if (remoteCompleteOutcome === "yielded") {
								logResumeYield("remote-complete-terminal-ownership-lost");
								return "yielded";
							}
							input.onProgress?.(session.fileSize);
							return "completed";
						}
						// Silent-failure rule: record the non-terminal verdicts too, so a future "why did it
						// re-complete?" is one log line instead of a reconstruction from indirect signals.
						if (remoteCompleteEvidence !== "probe-failed") {
							try {
								ports.telemetry.logInfo("recording.fileUpload.remoteCompleteProbeContinued", {
									sessionId: session.id,
									uploadId: session.uploadId,
									evidenceKind: remoteCompleteEvidence
								});
							} catch {
								// Observability cannot gate the completion that follows.
							}
						}
						const completed = await finalizeMultipartCompletion(
							{ session, parts, lease: leaseScope.lease },
							true,
							resumeAttempt.signal
						).catch(async (error) => {
							if (error instanceof UploadCancelledError && ports.policies.cancelEnabled && input.cancelSignal?.aborted) {
								await runGuardedEffect(leaseScope.lease, session, () =>
									ports.sessionStore.tombstoneCancelled(session)
								).catch((cleanupError) => {
									logResumeWarning(
										ports.telemetry,
										"recording.fileUpload.cleanupFailed",
										{
											sessionId: session.id,
											uploadId: session.uploadId,
											operation: "tombstone-cancelled-session"
										},
										cleanupError
									);
								});
							}
							throw error;
						});
						return completed ? "completed" : "yielded";
					}

					const prepared = await ports.persistedResume.prepareUpload({
						session,
						sourceReadStrategy: session.sourceReadStrategy ?? "slice",
						allChunkMeta,
						unuploadedMeta
					});
					if (prepared.kind === "retry") {
						return releasePersistedSessionForRetry(session, leaseScope.lease);
					}
					return await resumePersistedFileUpload({
						input: {
							session,
							allChunks: allChunkMeta,
							// Header-first on the resume path too: the chunk loop dispatches in input-array
							// order, so a resumed sealed upload re-sends the header before the payload
							// tail. A preference only — `orderPendingChunks` never drops or duplicates.
							unuploadedChunks: orderPendingChunks(unuploadedMeta, session.priorityChunkIndexesJson),
							chunkUrls: parsePersistedChunkUrls(session.chunkUrlsJson, ports.telemetry, session),
							chunkSource: prepared.chunkSource,
							onChunkComplete: (progress) => {
								reporter.signal();
								(input.onChunkComplete ?? prepared.onChunkComplete)?.(progress);
							},
							createTruncatedPartsError: prepared.createTruncatedPartsError,
							onProgress: input.onProgress,
							onActivity: () => reporter.signal(),
							cancelSignal: resumeAttempt.signal,
							leaseScope,
							behavior: {
								markPendingOnError: false,
								skipLeaseManagement: true,
								shouldTombstoneCancelled: () => input.cancelSignal?.aborted === true
							}
						},
						ports,
						finalizeMultipartCompletion: (completionInput) =>
							finalizeMultipartCompletion(completionInput, true, resumeAttempt.signal)
					});
				} catch (error) {
					if (error instanceof UploadCancelledError) {
						if (resumeAttempt.signal.aborted && !input.cancelSignal?.aborted) {
							return "retry";
						}
						throw error;
					}

					if (isConfirmedMigrationIntegrityError(error)) throw error;

					if (isUploadPartsIncompleteError(error)) {
						await throwIfUserCancelled();
						return handlePartsIncompleteRefusal({ session, error, lease: leaseScope.lease });
					}

					// Finding 14. `classifyFailure` calls an assembly-integrity failure "retry", which is
					// right for ONE pass — the parts are kept server-side and a transient copy fault can
					// verify next time — but every retry re-runs a full server-side assembly, so a
					// deterministic mismatch paid that cost on every drain wake and never reached the user.
					// Bounded here rather than in the apps' classifiers so both drivers get the same
					// ceiling from the one ledger. Below the bound the error falls through untouched to the
					// classifier and its release, exactly as before.
					if (isChunkedObjectAssemblyIntegrityError(error)) {
						const verdict = recordAssemblyIntegrityFailure({ session, error });
						if (verdict.exhausted) {
							await throwIfUserCancelled();
							return escalateUnproductivePartsIncomplete({
								session,
								error,
								lease: leaseScope.lease,
								ports,
								verdict,
								lane: "assembly-integrity"
							});
						}
						// CLAUDE.md "Diagnostic logging when fixing silent failures": a retried integrity
						// failure released the row with nothing naming how many assemblies it had already
						// cost, so the loop was invisible until the bound fired.
						console.warn("[Upload] Chunked-object assembly failed its integrity check; retrying within the bound", {
							sessionId: session.id,
							uploadId: session.uploadId,
							attemptId: session.attemptId,
							consecutiveFailures: verdict.consecutive,
							bound: MAX_CONSECUTIVE_ASSEMBLY_INTEGRITY_FAILURES
						});
					}

					if (isUploadVerificationPending(error)) {
						await throwIfUserCancelled();
						return releasePersistedSessionForRetry(session, leaseScope.lease, { error });
					}
					const failureTelemetryAlreadyEmitted = isMultipartUrlRefreshError(error);
					// LSP-23082: a /urls UPLOAD_NOT_FOUND is asked about once before it is classified, because the apps read
					// that code as auth-gated and a stale or unrevivable attempt then retried forever. The probe never
					// rebinds on its own evidence (see refusedAttemptProbe.ts); an "unproven" answer changes nothing.
					const refusedAttempt = await probeRefusedUploadAttempt({
						session,
						error: unwrapMultipartUrlRefreshError(error),
						lease: leaseScope.lease,
						multipartApi: ports.multipartApi,
						getById: (sessionId) => ports.sessionStore.getById(sessionId),
						telemetry: ports.telemetry
					});
					if (refusedAttempt.kind === "rebound") {
						await throwIfUserCancelled();
						return releasePersistedSessionForRetry(refusedAttempt.session, leaseScope.lease);
					}
					const failureError = refusedAttempt.kind === "unresumable" ? refusedAttempt.error : unwrapMultipartUrlRefreshError(error);
					const failureKind =
						refusedAttempt.kind === "unresumable" || isUploadTerminalFailure(failureError) || isUploadControlUnavailable(failureError)
							? "needs-attention"
							: ports.persistedResume.classifyFailure(failureError);

					if (
						isMultipartUrlRefreshError(error) &&
						ports.persistedResume.reconciliationDefer === "retry" &&
						(failureKind === "retry" || failureKind === "other")
					) {
						return releasePersistedSessionForRetry(session, leaseScope.lease, {
							error,
							failureKind: "url-refresh",
							alreadyLogged: true
						});
					}

					switch (failureKind) {
						case "retry":
						case "retry-slow":
							await throwIfUserCancelled();
							return releasePersistedSessionForRetry(session, leaseScope.lease, {
								error: failureError,
								failureKind,
								slowTier: failureKind === "retry-slow",
								alreadyLogged: failureTelemetryAlreadyEmitted
							});
						case "lost": {
							try {
								ports.telemetry.logWarn(
									"recording.fileUpload.terminalLost",
									{
										sessionId: session.id,
										uploadId: session.uploadId,
										s3Key: session.s3Key,
										evidenceKind: "classified-failure"
									},
									failureError
								);
							} catch {
								// A terminal cleanup verdict must not depend on telemetry availability.
							}
							const outcome = await runGuardedEffect(leaseScope.lease, session, () =>
								ports.sessionStore.discardIfUploadIdMatches(session.id, session.uploadId)
							);
							if (outcome === "skipped-not-owned") {
								logResumeYield("classified-lost-discard-ownership-lost");
								return "yielded";
							}
							return "lost";
						}
						case "terminal-read-failure":
							return decidePersistedTerminal(
								session,
								leaseScope.lease,
								{ kind: "read-failure", error: failureError },
								input.cancelSignal
							);
						case "terminal-local-gaps":
							// A persisted-byte-total contradiction is a LOCAL fact about bytes that no longer exist
							// anywhere, and it must NOT be decided from ListParts: both real terminal policies read a
							// contiguous listing as "the server already holds the whole object" and hand off —
							// contact-admin additionally deleting the only local copy — for a file whose own persisted
							// grid proves it is short, and CompleteMultipartUpload/handoff is unrecoverable afterwards.
							// This is the same "terminal AS LOSS, never as the listParts-bearing evidence" rule
							// reconciliationStateMachine.ts already applies to the server-side byte-total mismatch
							// (rounds 49/81). Mark the session terminal locally — round 51's whole point, so the drain
							// stops re-picking a session no retry can fix — and report the refusal as the terminal
							// "needs-attention" OUTCOME rather than throwing: a throw bypasses every consumer's
							// outcome mapper and lands in its generic error path, which then describes a durably
							// terminal session as a retryable failure (Cursor Bugbot, PR #3450). The reason string
							// stays on the persisted row, so the diagnostic the throw carried is not lost.
							if (failureError instanceof PersistedByteIntegrityError) {
								await throwIfUserCancelled();
								try {
									ports.telemetry.logError(
										"recording.fileUpload.resumeFailed",
										{
											sessionId: session.id,
											uploadId: session.uploadId,
											s3Key: session.s3Key,
											failureKind: "terminal-byte-integrity"
										},
										failureError
									);
								} catch {
									// Failure telemetry must not replace the originating byte-integrity refusal.
								}
								const byteIntegrityOutcome = await runGuardedEffect(leaseScope.lease, session, () =>
									ports.sessionStore.markNeedsAttention(
										session,
										ports.errors.normalize(failureError).message,
										failureError
									)
								);
								if (byteIntegrityOutcome === "skipped-not-owned") {
									logResumeYield("terminal-byte-integrity-ownership-lost");
									return "yielded";
								}
								// The needs-attention CAS can also be declined because a MORE authoritative
								// terminal verdict legitimately won the race (see SessionStore.markNeedsAttention).
								// That is a safe no-op for the WRITE, but "needs-attention" is then the wrong
								// thing to REPORT: the row's real terminal state is complete/bytes-gone, and a
								// consumer mapping this outcome would tell the user an upload that actually
								// finished "needs attention" (PR #3450 review). Report the status that really
								// won, through the outcome each of those statuses already produces elsewhere in
								// this driver, so every consumer's existing mapping applies unchanged.
								if (byteIntegrityOutcome === "already-complete") return "completed";
								if (byteIntegrityOutcome === "already-bytes-gone") return "bytes-gone";
								if (byteIntegrityOutcome === "already-cancelled") return "yielded";
								return "needs-attention";
							}
							return decidePersistedTerminal(
								session,
								leaseScope.lease,
								{
									kind: "local-gaps",
									missingBufferIndexes: [],
									missingMetadataCount: 1
								},
								input.cancelSignal
							);
						case "needs-attention": {
							await throwIfUserCancelled();
							if (isUploadTerminalFailure(failureError)) return parkLifecycleTerminal(session, leaseScope.lease, failureError, isMultipartUrlRefreshError(error) ? "urls" : "complete");
							try {
								ports.telemetry.logError(
									"recording.fileUpload.resumeFailed",
									{
										sessionId: session.id,
										uploadId: session.uploadId,
										failureKind: "needs-attention"
									},
									failureError
								);
							} catch {
								// Failure telemetry must not replace the originating resume error or alter recovery behavior.
							}
							const outcome = await runGuardedEffect(leaseScope.lease, session, () =>
								ports.sessionStore.markNeedsAttention(session, ports.errors.normalize(failureError).message, failureError)
							);
							if (outcome === "skipped-not-owned") return "yielded";
							// Same terminal-precedence carve-out the byte-integrity branch above already
							// applies: markNeedsAttention's CAS also declines when a MORE authoritative
							// terminal verdict (complete/bytes-gone) legitimately won the race. Bucketing
							// those into "retry" describes a finished — or confirmed byte-less — upload as
							// one that will be tried again (PR #3450 review / LSP-17735).
							if (outcome === "already-complete") return "completed";
							if (outcome === "already-bytes-gone") return "bytes-gone";
							if (outcome === "already-cancelled") return "yielded";
							if (isUploadControlUnavailable(failureError)) return "needs-attention";
							return "retry";
						}
						case "recover-lost":
							if (!(await leaseScope.lease.owns(session))) {
								logResumeYield("recover-lost-entry-ownership-lost");
								return "yielded";
							}
							return recoverLostPersistedSession({
								session,
								error: failureError,
								lease: leaseScope.lease
							});
						case "persistence-failure":
							throw failureError;
						case "other":
							await throwIfUserCancelled();
							await releasePersistedSessionForRetry(session, leaseScope.lease, {
								error: failureError,
								failureKind: "other",
								alreadyLogged: failureTelemetryAlreadyEmitted
							});
							throw failureError;
						default:
							ports.telemetry.logWarn("recording.fileUpload.unexpectedResumeFailureKind", {
								sessionId: session.id,
								failureKind
							});
							return releasePersistedSessionForRetry(session, leaseScope.lease, {
								error: failureError,
								failureKind: String(failureKind),
								alreadyLogged: true
							});
					}
				}
			};

			try {
				return await leaseScope.runWithLiveness(session, runResume);
			} finally {
				activityReporter?.stop();
				leaseScope.release(session);
			}
		});
	}

	return {
		controlFlow,
		async persistFileToIndexedDB(input) {
			const requiredChunks = requiredChunksOrThrow(input.file, input.target);
			return persistUploadSession({
				...input,
				fileCategory: input.fileCategory ?? "video",
				totalChunks: requiredChunks,
				phase: "pre-persist"
			});
		},
		async persistDeferredFileToIndexedDB(input) {
			const targetChunkCount = Math.ceil(input.file.size / CHUNK_SIZE);
			const validation = validateMultipartTarget({
				fileSize: input.file.size,
				targetChunkCount,
				chunkSize: CHUNK_SIZE
			});
			if (validation.ok === false) {
				throw ports.errors.createMultipartTargetValidationError(validation, input.file);
			}
			const requiredChunks = validation.requiredChunks;
			return persistUploadSession({
				...input,
				fileCategory: input.fileCategory ?? "video",
				totalChunks: requiredChunks,
				phase: "deferred"
			});
		},
		async attachUploadTargetToDeferredSession(input) {
			const session = await ports.sessionStore.getById(input.sessionId);
			// Below the getById await so the family probe keeps its current ordering; `input.target`
			// is this entry point's own target and is never replaced within the call.
			const targetAdapter = resolveUploadTargetAdapter(input.target);
			if (!session) {
				ports.telemetry.logWarn("recording.fileUpload.deferredTargetAttachYielded", {
					reason: "session-missing",
					sessionId: input.sessionId,
					uploadId: targetAdapter.opaqueId(input.target)
				});
				return "yielded";
			}
			assertValidMultipartTargetIdentity(input.target, "Deferred target attachment");
			assertExactPresignedUrls(targetAdapter.chunkUrls(input.target), session.totalChunks, "Deferred target attachment");
			const leaseScope = ports.leaseCoordinator.createScope("pre-persist", input.sessionId, input.leaseToken);
			if (!(await leaseScope.lease.claim(session))) {
				ports.telemetry.logWarn("recording.fileUpload.deferredTargetAttachYielded", {
					reason: "claim-lost",
					sessionId: session.id,
					uploadId: session.uploadId
				});
				return "yielded";
			}
			leaseScope.activate(session);
			// leaseScope.release(session) in the finally below only clears the IN-MEMORY lease map
			// entry — it never touches the durable row (asyncChunkedUploadDriver.ts's generation-scoped
			// release documents this explicitly: it must NEVER touch anything durable). claim() above
			// already committed a real "uploading" write, so EVERY exit from this try block that isn't
			// "attached" — a yield, or a throw from the attach itself — must durably demote that claim
			// back to pending, or the row is left claimed by a lease nobody heartbeats, undrainable by
			// ANY tab for the full stale-lease window (rounds 32/33/34 / LSP-17735: rounds 32/33 fixed
			// this for the yield exit only; round 34 found the identical gap on the throw exit — this
			// function has three exits from its try, not two, and the throw exit had no compensator).
			const demoteClaimedSessionBestEffort = async (context: string): Promise<void> => {
				// releasePending derives its expected-identity CAS from session.activeDrainId — but
				// `session` here is the object from THIS function's very first getById call, which
				// claim() above has since superseded with leaseScope.token (a fresh claim mints its own
				// token; a concurrent exit handoff can also have changed activeDrainId during that first
				// getById's own await). Passing the stale session verbatim makes the CAS compare the
				// row's CURRENT (just-claimed) token against the OLD one and silently decline —
				// releasePending returns void, so that failure is invisible. Reflect the identity
				// claim() actually just committed.
				const claimedSession = { ...session, activeDrainId: leaseScope.token };
				// Deliberately NOT wrapped in runGuardedEffect's leaseScope.lease.owns() gate — for
				// contact-async's "pre-persist" scope, owns() is an IN-MEMORY predicate
				// (activeFileUploadLeases generation matching), not a durable one: a concurrent exit
				// handoff's bare releaseFileUploadSessionActive() (asyncChunkedUploadDriver.ts) deletes
				// that in-memory entry WITHOUT touching the durable row, flipping owns() to false while
				// IndexedDB still says this claim is unambiguously ours. The "skipped-not-owned" yield
				// exit this demote exists to compensate is PRODUCED by that exact same owns()-false
				// condition — gating the demote behind runGuardedEffect made it structurally dead on
				// that exit (round 42 / LSP-17735): it can only ever run for the OTHER yield source
				// (attachTarget's own store-level CAS declining, where owns() is still true), silently
				// skipping the demote for the in-memory-lease-torn-down case and leaving the row
				// undrainable for the full stale-lease window. Calling releasePending directly is safe
				// by construction, not omission: markFileUploadSessionPending (db.ts) already carries
				// its own activeDrainId + uploadId + terminal-status CAS, so a row a genuine successor
				// has since re-claimed still declines there — the in-memory gate contributed nothing but
				// this false negative.
				await ports.sessionStore
					.releasePending(claimedSession)
					.catch((cleanupError) => logBestEffortFailure(context, session, session.fileName, cleanupError));
			};
			try {
				let attachmentLostOwnership = false;
				const outcome = await runGuardedEffect(leaseScope.lease, session, async () => {
					const attachment = await ports.sessionStore.attachTarget(
						session,
						input.target,
						leaseScope.token,
						input.leaseToken !== undefined
					);
					if (isTargetAttachmentResult(attachment)) {
						attachmentLostOwnership = attachment.kind === "skipped-not-owned";
					} else if (!(await leaseScope.lease.owns(session))) {
						// Legacy adapters return only the session, so ownership is the only
						// available signal that their awaited CAS lost the race.
						attachmentLostOwnership = true;
					}
				});
				if (outcome === "skipped-not-owned" || attachmentLostOwnership) {
					ports.telemetry.logWarn("recording.fileUpload.deferredTargetAttachYielded", {
						reason: "not-owned",
						sessionId: session.id,
						uploadId: session.uploadId
					});
					// Always demoted, even when claim() above merely REUSED a lease this scope already
					// held (no gate on "was this a fresh claim") — an earlier version of this fix skipped
					// the demote for a reused claim, reasoning "releasing an already-held claim would tear
					// down a live claim this same tab still needs elsewhere." That reasoning does not hold
					// in practice: leaseScope.activate() above (asyncChunkedUploadDriver.ts's
					// keepActiveFileUploadSessionLeaseAlive) always installs a NEW heartbeat generation on
					// activation, even when reusing the same token, stopping whatever heartbeat was running
					// before this call — and the unconditional leaseScope.release(session) below then tears
					// that new generation down too. So a reused claim's heartbeat is already dead by the
					// time this branch runs regardless of what happens next; "skip the demote to protect
					// it" protects nothing, while leaving the durable row falsely claimed by a token nobody
					// heartbeats — exactly the harm this whole branch exists to prevent (round 33 /
					// LSP-17735). The sole real caller (OptionsPage's attachUploadTargetsToPersistedSessions)
					// always treats a yielded attach as "this file is no longer this tab's responsibility"
					// and releases its own tracking regardless of whether the claim was fresh or reused, so
					// unconditional demotion here matches the only path that actually exists.
					await demoteClaimedSessionBestEffort("release-claimed-session-after-attach-yield");
					return "yielded";
				}
				return "attached";
			} catch (attachError) {
				// The attach itself threw (e.g. a transient IndexedDB failure, or the session row
				// disappearing mid-write — asyncSessionStore.ts's attachTarget throws by name for both).
				// Without this, claim()'s durable write survives every rejection path: the sole caller
				// (OptionsPage.attachUploadTargetsToPersistedSessions) does not wrap this call, so the
				// rejection propagates out of handleUploads to the dispatch-level catch, which only ever
				// bare-releases the IN-MEMORY tracking (releaseAllLiveBatchSessions) — never anything
				// durable. Demote here, before rethrowing, so a throw leaves the row exactly as
				// recoverable as a yield does (round 34 / LSP-17735).
				await demoteClaimedSessionBestEffort("release-claimed-session-after-attach-throw");
				throw attachError;
			} finally {
				leaseScope.release(session);
			}
		},
		async uploadFile(input) {
			const requiredChunks = requiredChunksOrThrow(input.file, input.target);
			const targetAdapter = resolveUploadTargetAdapter(input.target);
			return controlFlow.runUploadOnceByUploadId(
				targetAdapter.opaqueId(input.target),
				input.cancelSignal,
				() => uploadFileDirect(input, requiredChunks),
				// A `/uploads` target has no S3 coordinates to report; reporting them as "" made every
				// `/uploads` identity compare on file size alone. See UploadSingleFlightIdentity.
				isUploadsUploadTarget(input.target)
					? {
							kind: "uploads",
							registryId: input.target.registryId,
							fileName: input.file.name,
							fileSize: input.file.size
						}
					: {
							kind: "legacy",
							s3Key: input.target.s3Key,
							bucket: input.target.bucket,
							fileSize: input.file.size
						}
			);
		},
		resumeFileUpload: (input) =>
			controlFlow.runUploadOnceByUploadId(
				input.session.uploadId,
				input.cancelSignal,
				(attempt: UploadSingleFlightAttempt) =>
					resumePersistedFileUpload({
						input: {
							...input,
							behavior: {
								...input.behavior,
								isSuperseded: attempt.isSuperseded
							}
						},
						ports,
						finalizeMultipartCompletion: (completionInput) =>
							finalizeMultipartCompletion(completionInput, true, input.cancelSignal)
					}),
				// The persisted row's `s3Key`/`bucket` are the same empty sentinels for EVERY `/uploads`
				// row, so the durable resume path degraded exactly as the direct path did. The row
				// already carries the family marker and its registry identity — read the comparison off
				// those instead of changing what the stores write.
				getUploadEndpointFamily(input.session) === "uploads"
					? {
							kind: "uploads",
							// Pre-marker `/uploads` rows can lack `registryId`; `uploadId` deliberately
							// mirrors it in all three stores and is this row's lookup key either way.
							registryId: input.session.registryId ?? input.session.uploadId,
							fileName: input.session.fileName,
							fileSize: input.session.fileSize
						}
					: {
							kind: "legacy",
							s3Key: input.session.s3Key,
							bucket: input.session.bucket,
							fileSize: input.session.fileSize
						}
			),
		resumePersistedMultipartSession,
		finalizeMultipartCompletion,
		reinitializeSession,
		async discardLostSession({ sessionId, uploadId }) {
			await ports.sessionStore.discardIfUploadIdMatches(sessionId, uploadId);
		}
	};
}
